import { create } from 'zustand';
import { genreOf } from '../lib/genre';
import { PAYMENT_METHOD_POINTS, WEB3_JPYC_PAYMENT } from '../lib/paymentMethods';
import { auth } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import { api } from '../services/api';
import { getDeviceId, clearLocalStoragePreservingDeviceId } from '../lib/deviceId';
import { clearGuestId, getGuestId } from '../lib/guestId';
import { maxQuantityFor } from '../lib/partySize';
import {
  cartLineId, groupsForItem, selectionFromSnapshot, selectionPrice, selectionText, snapshotText,
  type VariationGroup, type VariationSelection, type VariationSnapshot,
} from '../lib/menuVariation';
import { logoutWeb3Auth } from '../lib/web3auth';
import { isWeb3Mode } from '../lib/authMode';
import { getJpycBalance, transferJpyc } from '../lib/jpyc';
import { getEthersProvider } from '../lib/ethers';
import { getExplorerTxLink } from '../lib/network';
import { resetAnnouncementState } from '../lib/announcementFrequency';
import type { RolePolicy } from '../lib/rolePolicy';
import i18n, { normalizeLocale } from '../i18n/i18n';
import { selectLocalizedName } from '../lib/itemName';

// Types
export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  // #2005 '' = 未回答。登録直後はここが空で、プロフィール登録で選ぶまで埋まらない
  // (以前は 'US' を焼いていたため、離脱した人がアメリカとして数えられていた)
  nationality: '' | 'JP' | 'US' | 'TW' | 'FR' | 'KR' | 'CN' | 'OTHER';
  // #2005 '' = 未回答。**自分で言語を選んだときだけ**埋まる (国籍から自動で
  // 決まった値は入れない — FR/KR は 'en' に落ちるので、その人たちが
  // English として数えられてしまう)
  language: '' | 'ja' | 'en' | 'zh' | 'ko' | 'fr' | 'de' | 'es' | 'th' | 'vi' | 'id';
  dietaryAllergens?: string[];
  dietaryStyles?: string[];
  // Self-order display settings (#314 task #4). Customer chooses what the
  // store sees on their order card: anonymous tag (default), real name, or
  // a nickname the user sets.
  nickname?: string;
  displayPreference?: 'tag' | 'name' | 'nickname';
  // #1207 紙レシート設定: アプリ決済時に店頭で自動印刷するか。既定 false (不要 —
  // アプリでいつでも見られる)。ProfileSetup / Profile 設定で変更。
  wantsPaperReceipt?: boolean;
  // #1274 セグメント推薦用の任意の自己申告属性 ('' = 未回答)。
  // 「同じ年代/同じ国の旅行者の実績」の重み付けにのみ使う
  ageBand?: '' | '10s' | '20s' | '30s' | '40s' | '50s' | '60s+';
  gender?: '' | 'female' | 'male' | 'other';
  /** #2004 居住地 (任意・自己申告。'' = 未回答)。県と市は必ず 2 つ一組 */
  residencePrefecture?: string;
  residenceCity?: string;
  /** #2004 地域のお知らせメールを停止した (既定 false = 受け取る。同意は居住地の登録で取る) */
  residenceMailOptOut?: boolean;
}

export interface Wallet {
  address: string;
  balanceJPYC: number;
  balanceUSD: number;
  chainBalanceJPYC: number | null;
}

export interface MenuItem {
  id: string;
  name: string;
  nameEn: string;
  price: number;
  imageUrl: string;
  /**
   * 物販の写真ギャラリー。**先頭は imageUrl と同じ**メイン写真（サーバが揃える）。
   *
   * 一覧・レジ・レシート・お気に入りは imageUrl しか見ない。ここを見るのは
   * 商品を開いたときだけ。旧データには無いので、表示側は imageUrl 1 枚へ落とす。
   * 枚数の上限は運営が決める（`Store.retailMaxImages`）。
   */
  imageUrls?: string[];
  description?: string;
  allergens?: string[];
  dietary?: string[];
  // #288 AI 推定メタデータ。allergenSource: 'ai' = AI 推定を無編集で保存 /
  // 'manual' = 店舗が確認・編集済み。needsAllergenReview: 低信頼度の AI 推定を
  // 未確認のまま保存した印 — 顧客側に「店舗にご確認ください」を表示する。
  // allergenAiEstimatedAt/-Model: 監査用 (いつ・どのモデルが推定したか)。
  allergenSource?: 'manual' | 'ai';
  needsAllergenReview?: boolean;
  allergenAiEstimatedAt?: string;
  allergenAiModel?: string;
  category?: string;
  // #970 売り切れフラグ。true の品は顧客側で「売り切れ」バッジ＋カート追加不可。
  soldOut?: boolean;
  // #1353 店主のおすすめ。**カテゴリとは別軸**。カテゴリは「何の料理か」の排他的な
  // 分類、おすすめは「店が推したいか」の強調。同じ軸に混ぜると、おすすめにした
  // 瞬間に本来の分類が失われ、その品がメインのセクションから消える。
  recommended?: boolean;
  // Step 1 Phase 1b (#6): per-locale translations for customer-side rendering.
  // Populated by the translation service on menu save; absent entries fall
  // back to `nameEn` (for EN) or `name` (for JA).
  translations?: Record<string, { name: string; description?: string }>;
  // #2028 商品名の読み (ひらがな)。客に配る店の情報にだけ付く (サーバが名前から作る。
  // **保存はしていない** — services/pay/api/services/menu_reading.py)。メニュー検索で使う
  nameReading?: string;
  // #1182/#1181 物販 (お土産・グッズ) 拡張。isRetail: 物販商品フラグ — 店舗管理の
  // 「商品」タブから登録した item に付き、スキャン販売/QRラベル/EC連携の対象になる
  // (どのタブで登録したかが決める。ユーザーにチェックボックスは見せない)。
  // barcode: JAN/EAN or 任意コード (店内一意)。stock: 数量在庫 (null/未設定 =
  // 在庫管理しない。0 で soldOut 自動連動)。cost: 原価 (税込・参考表示のみ)。
  isRetail?: boolean;
  barcode?: string | null;
  stock?: number | null;
  cost?: number | null;
  // #1171 軽減税率 8% の対象になりうるか (= 飲食料品)。持ち帰りの時だけ 8% になる。
  // 未設定はタブで決まる — 飲食メニューは対象、物販は対象外。
  // **酒類は false**、**食品のお土産は true** にする。
  reducedTaxEligible?: boolean;
  // #1181 follow-up 発注点アラート。reorderPoint: 在庫がこれ以下になった瞬間に
  // 1回だけ発注担当へ通知する閾値 (null/未設定 = アラートしない。オプトイン)。
  // supplier*: 通知メールに差し込む発注先情報 (表示専用・挙動なし)。
  reorderPoint?: number | null;
  supplierName?: string | null;
  supplierTel?: string | null;
  supplierNote?: string | null;
  supplierId?: string | null;
  // #1379 PR C 1ケースあたりの入数。**在庫の単位は変えない** — 在庫は常にバラの個数で、
  // これは入荷画面で「2ケース」と入れるための換算値にすぎない。
  caseQuantity?: number | null;
  // #1379 PR C セット商品（詰め合わせ）の構成。設定するとこの商品は**自分の在庫を
  // 持たず**、売れたら子の在庫が減る。作れる数は lib/retail.ts の buildableSetCount。
  components?: { itemId: string; quantity: number }[] | null;
  // #2031 この商品が使うバリエーショングループの ID (例: 焼き鳥 → ['aji'])。
  // **定義は店に 1 つ** (Store.variationGroups) で、ここは参照だけ。lib/menuVariation.ts
  variationGroupIds?: string[] | null;
  // #1184 自社EC (ec_products) への明示リンク。
  ecProductId?: string | null;
  // 二重価格表示の比較対照価格。設定すると客側に「¥1,100 → ¥550 (50%OFF)」と出る。
  //
  // **名前を listPrice にしていない**のは、注文明細の `listPrice`
  // (services/pay/api/services/all_you_can.py — 食べ放題で ¥0 になった品の「本来の通常価格」)
  // と別概念だから。同じ名前だと、レシートの listPrice を見た人が
  // 希望小売価格と取り違える。
  //
  // compareAtPriceKind は**何の価格か**。景品表示法の二重価格表示では、比較対照が
  // 何であるかを明示しないと有利誤認になりうるため、客側の表示にも必ず出す。
  // 'manufacturer' はメーカーが実際に設定・公表している希望小売価格がある場合のみ。
  // オープン価格の商品に希望小売価格があるかのように見せるのは不当表示。
  compareAtPrice?: number | null;
  compareAtPriceKind?: 'manufacturer' | 'store';
  // #1711 キャンペーンの名称と終了日時。全部任意で、無ければ従来どおり「N%OFF」だけ出る。
  // 終了日時 (ISO 8601, tz 付き) を過ぎると**サーバが読み取り時に通常価格へ戻す**
  // (services/pay/api/services/menu_campaign.py)。画面側も lib/menuCampaign.ts で同じ判定を
  // 持ち、開いたまま期限をまたいだ端末で古い価格を見せない。
  campaignName?: string | null;
  campaignNameEn?: string | null;
  campaignEndAt?: string | null;
}

// #329 キッチンカーの出店枠 1 件 (場所 + 時間帯)。時刻は "HH:MM"。
export interface KitchenCarSpot {
  placeName: string;
  latitude: number;
  longitude: number;
  openTime: string;
  closeTime: string;
}

// #329 出店スケジュール。解決 (今日どこか) は backend services/kitchen_car.py。
export interface KitchenCarSchedule {
  enabled: boolean;
  weekly: Partial<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', KitchenCarSpot | null>>;
  dates: Record<string, KitchenCarSpot | null>;
}

export interface Store {
  id: string;
  name: string;
  isTrial?: boolean;  // #2140 紹介ページの体験の店 (来た人ごとに作る。一覧に出ない)
  nameEn: string;
  /**
   * #1661 **飲食の細分類だけ。** 店の業態は genre が持つ (lib/genre.ts)。
   * 非飲食の店は持たない。'shop' / 'hotel' / 'other' は業態であって料理の種類では
   * ないので外した (宿は #1666 が動くとき genre に足す)。
   */
  category?: 'restaurant' | 'cafe' | 'izakaya' | 'bar' | 'ramen' | 'sushi' | 'bakery' | 'sweets' | 'kitchencar';
  // #1082 店ジャンル（体験/飲食/お土産/その他）。オーナーが選ぶ上位区分。未設定店は
  // backend が category から導出して返す（firebase_service._ensure_genre）。
  genre?: 'experience' | 'food' | 'souvenir' | 'other';
  address: string;
  prefecture?: string;
  city?: string;
  // #696: 建物名・部屋番号 (任意). `address` は都道府県+市区町村+町名・番地までの
  // 3 段構成。building は独立フィールドとして保存し、UI 表示時に末尾に空白区切り
  // で append する。reverse geocode (#695) は building を上書きしない。
  building?: string;
  areaTags?: string[];
  distance: number;
  rating: number;
  reviewCount: number;
  shareCount?: number;
  imageUrl: string;
  // #971 店舗ロゴ（QR卓上カード等のブランディング用）。カバー画像 imageUrl とは別。
  logoUrl?: string;
  // #971 セルフオーダー初期画面（ウェルカム）専用の歓迎写真。未設定時は imageUrl に
  // フォールバック。
  selfOrderImageUrl?: string;
  hours: string;
  // #2136 お店のレジで使える支払い。**アプリでの支払いとは別軸** —
  // YOKOSO PAY は paymentEnabled から自動で出る。こちらは店しか知らないので、
  // 設定されたものだけを出す (未設定なら「お店で支払う」の行ごと出さない)。
  storePaymentMethods?: Array<'cash' | 'credit_card' | 'emoney' | 'qr_code'>;
  // #2136 Wi-Fi。**三値** — true=あり / false=なし / undefined=言わない。
  // 以前は全店に「Free Wi-Fi あり」と出ていた。
  wifiAvailable?: boolean;
  // #2136 公式サイト。客が「もっと見たい」ときの行き先。
  websiteUrl?: string;
  // #2136 公式サイト以外のリンク (食べログ・予約サイト・公式 LINE 等)。
  // **載せたい先は店ごとに違う**ので種類を決め打ちしない。名前は店が付ける。
  otherLinks?: Array<{ label: string; url: string }>;
  // #2134 定休日 (任意)。空・未設定なら客の画面に行ごと出さない。
  // 以前は「定休日: 不定休」が全店で固定表示されていた (店のデータを見ていなかった)。
  closedDays?: string;
  // #1043: 店舗電話番号（任意）。予約一覧の期限切れキャンセル案内等で tel: リンクとして使用。
  phoneNumber?: string;
  latitude: number;
  longitude: number;
  // #329 キッチンカーの出店スケジュール (オーナーが店舗設定で編集)。
  // weekly: 曜日パターン (mon..sun、無い曜日は休み) / dates: 日付上書き (null=休み)
  kitchenCarSchedule?: KitchenCarSchedule | null;
  // #329 backend decoration (顧客向け read のみ)。キーの有無で 3 状態:
  // undefined = スケジュール運用していない従来店 / null = 本日出店なし /
  // object = 今日の出店枠 (latitude/longitude は既にこの位置へ差し替え済み)
  kitchenCarToday?: KitchenCarSpot & { isOpenNow: boolean } | null;
  menu: MenuItem[];
  qrCode: string;
  walletAddress?: string;
  // #711: 2 軸の表示制御。旧 `visible` / `testOnly` の置換。
  // - discoverability: 'public' (検索 list に出る) | 'unlisted' (QR/URL 直リンクのみ)
  // - operability:     'open'   (営業中)            | 'paused'   (臨時休業)
  // legacy doc は backend filter 側で default 'public' / 'open' 扱い。
  discoverability: 'public' | 'unlisted';
  operability: 'open' | 'paused';
  // #686: owner の Stripe Connect KYC 状態 (`charges_enabled`) を反映。
  // `/stores/{id}` と `/stores/nearby` の両方が付ける (#1769)。一覧には現金のみ運用の店が
  // KYC 未完了でも載るので false があり得る。客側の支払手段の判定は lib/storePayment.ts。
  paymentEnabled?: boolean;
  // #721: 任意の Google Maps URL (例: https://maps.app.goo.gl/...)。設定すると
  // 顧客側 InfoTab の「Google Maps で開く」がこの URL に飛び、店舗の Google
  // ビジネスプロフィール (= レビュー / 写真) を表示できる。未設定なら住所
  // テキスト search に fallback。null は明示的に未設定を示す (Firestore で
  // null/undefined どちらも許容するため `string | null` 型)。
  googleMapsUrl?: string | null;
  // #1169 適格請求書 (インボイス) 発行事業者 登録番号 (T+13桁)。設定すると
  // 紙レシート・Web領収書に印字される。
  invoiceRegistrationNumber?: string | null;
  /** #1867 レシートの下に刷る、店が好きに使える QR。用途は店が決める
   *  (Google マップのレビュー誘導 / 地域のイベント案内 等)。 */
  receiptQr?: { url?: string | null; captionJa?: string | null; captionEn?: string | null } | null;
  // #1880 レシートの固定メッセージ (取引番号・担当の下に印字)
  receiptMessage?: { ja?: string | null; en?: string | null } | null;
  // #1173 Stripe Terminal (S700 店頭リーダー)。readerId があれば店頭カード会計可。
  terminal?: {
    locationId?: string;
    readerId?: string;
    readerLabel?: string;
  } | null;
  // #1178 会計の受け渡しスタイル。客側のレジ誘導文言が切り替わる。
  checkoutStyle?: 'staff_brings' | 'customer_register' | null;
  // #1401 お通し・席料の定型キー。レジからワンタップで伝票に入れる。
  // perPerson = 人数分課金 (お通し代 × 人数)。テーブルチャージは定額なので false。
  serviceCharges?: ServiceChargePreset[];
  // #1171 軽減税率 8% の内訳を出すか。opt-in で、OFF の店は全部 10% のまま。
  reducedTaxEnabled?: boolean;
  // #1410 レジの値引きをオーナー・店長に限るか。**既定 false = スタッフも可**。
  // Airレジは値引きを権限の対象にしておらず、Square/Toast/スマレジも「役割ごとに
  // 店が決める」形。閉めるかどうかは店の判断で、こちらでハードコードしない。
  discountOwnerOnly?: boolean;
  // #288 メニュー AI 推定 (アレルゲン・食事制限) の免責事項に同意した日時 (ISO)。
  // 未設定なら「AI で推定」の初回実行前に同意モーダルを出す。
  allergenAiDisclaimerAckedAt?: string;
  snsMedia?: {
    coverImageUrl?: string;
    promoVideoUrl?: string;
    instagramHandle?: string;
    twitterHandle?: string;
    /** Customer-facing share caption set by the store owner. When present,
     *  shareToX / shareToLine / shareToWhatsApp use this instead of `name`. */
    shareText?: string;
    /** Curated image — attached via the Web Share API path (mobile) and used
     *  as the own-post image button's source in ShareButtonsRow. */
    shareImageUrl?: string;
    /** #69 店舗が「これを広めてほしい」と決めた X 公式ポストの URL。
     *  顧客側シェア UI の主導線 (1タップリポスト) の誘導先。 */
    xPostUrl?: string;
    /** #69 同上の Instagram 版 — 「投稿を開く → ストーリーズに追加」導線の誘導先。 */
    instagramPostUrl?: string;
    /** #69 店舗カスタムハッシュタグ (`#` なしの正規化済みトークン)。
     *  ユーザー投稿トラックで COMMON_HASHTAGS と結合して自動付与される。 */
    hashtags?: string[];
    /** Legacy (#69 以前の1本URL)。読み取りは `resolveOfficialPostUrls` が
     *  ドメイン判定で xPostUrl / instagramPostUrl に振り分けてフォールバック
     *  する。StoreSettings の SNS タブを保存した時点で書かれなくなる。 */
    sharePostUrl?: string;
  };
  customCategories?: CustomCategory[];
  /**
   * 物販商品 1 点あたりの写真の枚数上限。**運営が決める値をサーバから受け取る。**
   *
   * 店舗設定ではない（写真の保管・配信は当社の費用なので店には決めさせない）。
   * ここに持たず画面へ定数で書くと、サーバ側を env で変えたときに画面だけ
   * 古い数のままになる。未設定の応答（旧サーバ）では 1 枚として扱う。
   */
  retailMaxImages?: number;
  reservationEnabled?: boolean;
  reservationConfig?: ReservationConfig;
  // #1439 オーナーが決める「誰に何を見せるか」。未設定なら lib/rolePolicy の既定値で解決。
  rolePolicy?: RolePolicy;
  // #2115 'menu-only' = メニューを見せるだけの店。注文も会計も他社の仕組みで
  // 回っていて連携できない店 (POS・他社モバイルオーダー) の入口。決済・カート・
  // 「現金のみのお店です」の案内を**出さない**。注文は externalOrderUrl へ送る。
  orderingMode?: 'pay-only' | 'self-order' | 'menu-only';
  // #2121 掲載の区分。'agency' = 弊社が店の許諾をもらって載せている店 (#2117)。
  // 未設定・'store' は店が自分で登録した店 (今までどおり)。客の画面には
  // 「YOKOSO PAY が掲載しています」の一文が出る — 弊社が出している情報なので
  // どこの情報かを隠さない。許諾の記録・引き渡し先・担当者メモは**公開されない
  // 別コレクション** (store_agency_listings) にあり、ここには来ない。
  listingManagedBy?: 'store' | 'agency';
  // 価格・メニューを最後に確かめた日 (YYYY-MM-DD)。代行掲載のときだけ入る。
  listingCheckedAt?: string;
  // #2115 「ご注文はこちら」が開く先 (店で 1 本の共通 URL)。`{table}` は卓番号に
  // 置き換わる (例: https://example.com/{table}/menu)。空ならボタンを出さない。
  externalOrderUrl?: string;
  // #2115 卓ごとの URL。**実運用はこちらが主**。卓番号 (文字列) → URL。
  // 卓ごとの URL は「その卓の QR から来たお客様」にだけ出す (店の外から
  // 注文されるのを防ぐ)。共通 URL より優先する。
  externalOrderUrlsByTable?: Record<string, string>;
  // #314 task #2: number of physical tables that have a printable QR. 0 or
  // unset means table QRs are not in use (the regular store-detail QR still
  // works). Capped server-side at 50.
  tableCount?: number;
  // REQ-28 (#1536) 1 組で受けられる最大人数。オーナー・店長が設定でき、
  // 未設定なら 50 (lib/partySize.ts)。放題の適用・増減と予約の受付人数が
  // すべてこの値を上限にする。サーバでも検証される。
  maxPartySize?: number;
  // #284 出前（店舗自前配達）. Opt-in per store; hidden from the /delivery flow
  // unless deliveryEnabled === true. Range is applied client-side via
  // haversine against the user's current location.
  deliveryEnabled?: boolean;
  // #1213 店頭受け取り (takeout)。orderingMode とは別軸で、既存の飲食店が
  // 「持ち帰りだけ」ON にできる。先払い + 呼び出し番号で受け渡す。
  takeoutEnabled?: boolean;
  // #1353 お土産（物販）タブを顧客に見せるか。在庫管理だけに使いたい店があるため
  // 店主が切れる。未設定は表示（既存店の見え方を変えない）。
  retailVisibleToCustomers?: boolean;
  // #1181 follow-up 在庫アラート（発注点通知）の送信先。**オーナーの登録アドレスとは
  // 別に設定できる** — 発注担当は決済オーナーと別人のことが多く、shiire@ のような
  // 在庫管理専用の共有アドレスを使う店がある。未設定/空ならオーナー宛にフォールバック。
  reorderNotifyEmails?: string[];
  deliveryRadiusKm?: number;   // 配達可能半径（km）
  deliveryMinOrder?: number;   // 最低注文額（円、税込）
  deliveryFee?: number;        // 配達料金（円、固定額）
  // #972 現金のみ運用モード（オーナー手動）。true で KYC 未完了でも一覧表示・利用でき、
  // アプリ内カード決済は無効（現金会計のみ）。出前・予約のカード依存機能も無効化される。
  // paymentEnabled（KYC=Stripe chargesEnabled の自動反映・読取専用）とは別軸。
  cashOnlyMode?: boolean;
  // #2162 店の端末 (STORES 決済・Airペイなど他社の決済端末) をレジで使うか。会社名は任意のメモ
  storeTerminalEnabled?: boolean;
  storeTerminalMemo?: string;
  // #2181 他社の決済端末の手数料率 (%、例 3.24)。売上の内訳の手数料の目安に使う
  storeTerminalFeeRate?: number | null;
  // #1438 YOKOSO ポイント払いを受けるか。**未設定 = 受ける**（既定を false にすると
  // 今ポイントを受けられている店で静かに決済が壊れる）。判定は必ず `!== false` で行う。
  // 自治体・企業ポイント（#1433）は発行体ごとに加盟関係が違うので別軸になる。
  acceptsPoints?: boolean;
  // #2036 卓の滞在時間 (分)。客への「ご利用時間 / ラストオーダー」の案内の元。**未設定は
  // 180 分**。卓セッションの寿命は別で、短くしても今までどおり最低 4 時間もつ。
  tableStayMinutes?: number;
  // ラストオーダーを案内する「終了の何分前か」。未設定は 30 分前。
  tableLastOrderNoticeMinutes?: number;
  // 滞在時間を過ぎたら**客の**注文を止めるか。既定 OFF (時間制の店だけ ON)。
  // 店員の代理入力は止まらない。
  tableStopOrdersAtStayLimit?: boolean;
  // #2039 伝票の単位。未設定 = 'table' (今までどおり卓)。'binder' = 番号付きのバインダーに
  // 伝票を付ける店 (時間制バー・エンタメバー・相席系)。**バインダー番号は卓番号の枠に入る**
  // ので、枚数は tableCount、注文・会計の tableNumber がバインダー番号になる。
  billUnit?: 'table' | 'binder';
  // バインダーの種別。番号が start〜end のものに planId のプランを付ける。
  // allFree = 全員無料 (レディース・イベントなど)。アプリは性別を見ない。
  binderTypes?: BinderType[];
  /** #2039 バインダー形式の店で、客のスマホからの注文を受けるか (既定は受けない) */
  binderCustomerOrder?: boolean;
  /** #2054 営業日の切り替え時刻 (JST の時・0〜12)。0 / 未設定 = 暦日 */
  businessDayStartHour?: number;
  // #2029 客に見せるメニューの最初の表示。'grid' = 画像 (3 列のカード) / 'list' = リスト。
  // **未設定は画像** (今までどおり)。客は画面で切り替えられ、店ごとに端末に記憶される。
  menuLayout?: 'grid' | 'list';
  // #2031 バリエーション (味・サイズ・トッピング) の定義。商品から ID で参照する。
  variationGroups?: VariationGroup[] | null;
  // #1184 自社EC vendor リンク (admin が ec_vendors.payStoreId を設定すると非正規化される)。
  ecVendorId?: string | null;
  ecVendorSlug?: string | null;
  // #1748 その EC 出店者が海外配送をやるか。**未設定は「やらない」扱い**
  // (vendor 側の既定が false なので、リンクを貼り直していない古い店で
  //  「海外配送でお届け」と誤って出るより、控えめな文言に倒す方が安全)。
  ecCrossBorderAllowed?: boolean | null;
}

// お気に入り店（#1078）。一覧を店マスタ非依存で描画するため、店の最小スナップショット
// を保持する（旅行者が別エリアに移動して近隣一覧から外れても表示できる）。
export interface FavoriteStore {
  storeId: string;
  name: string;
  nameEn?: string;
  imageUrl?: string;
  /** #1661 飲食の細分類。**業態は genre を見る** (非飲食の店は持たない)。 */
  category?: Store['category'];
  /** #1661 業態。お気に入り一覧のアイコン・ラベルはこれで引く。 */
  genre?: Store['genre'];
}

export interface CustomCategory {
  key: string;
  label: string;
  // #2115 カテゴリの説明。「世羅みのり牛とは」のように、**なぜこの食材が特別なのか**を
  // 伝えるための短い文。メニューの見出しの下に出る。未設定なら何も出ない。
  // 英語だけ別に持つ (他の言語は en → ja の順に落ちる。アプリ全体の決まりと同じ)。
  description?: string;
  descriptionEn?: string;
  // #2115 カテゴリ名の英語。translations が無い言語はここ → 日本語 の順に落ちる。
  labelEn?: string;
  /** #2115 10 言語の訳 (品名と同じ形)。`{ ja: {label, description}, en: {...}, ... }` */
  translations?: Record<string, { label?: string; description?: string }>;
}

/** #1401 お通し・席料の定型キー（レジのワンタップ追加用）。 */
export interface ServiceChargePreset {
  id: string;
  name: string;
  price: number;
  /** true = 人数分課金（お通し代 × 人数）。false = 定額（テーブルチャージ等）。 */
  perPerson: boolean;
  /** #1529 卓が開いたら自動で精算に付ける（お通しの押し忘れ対策）。既定 false */
  autoApply?: boolean;
}

export interface ReservationConfig {
  availableDays: number[];   // 0=Sun ... 6=Sat
  timeRange: { start: string; end: string };  // "17:00" - "20:30"
  slotInterval: number;      // minutes
  partySizeRange: { min: number; max: number };
  // #1386 席の占有時間。以前は「1 組独占時間」だったが、席数で受けられるように
  // なったので**席を押さえる長さ**を意味する。コースの durationMinutes は
  // 飲み放題の時間管理用で、席の占有には使わない。
  durationMinutes: number;
  // #1357 何日先まで予約を受け付けるか。未設定は DEFAULT_BOOKABLE_DAYS_AHEAD (30)。
  // 顧客の日付候補の生成と、サーバ側の受付範囲チェックの両方がこの値を見る。
  bookableDaysAhead?: number;
  /** 直前予約の下限 (分)。未設定なら既定 60 分。0 = 制限なし (過去時刻は常に不可)。 */
  minLeadMinutes?: number;
  // #1386 同じ時間帯に予約で受けられる席数の上限。
  //
  // 未設定だと従来どおり「1 件の予約で時間帯が全滅」する（上限を知らないまま
  // 何組でも受けると入りすぎるため安全側）。実席数より少なく設定するのが実務で、
  // 例えば実席 25 なら 20 にして 5 席を飛び込み用に残す。
  reservableSeats?: number;
  // #737: 決済タイミング設定 (店舗単位、コース予約と通常予約の両方に適用)
  paymentTiming?: 'prepaid' | 'deposit' | 'onsite';
  depositMode?: 'percent' | 'fixed';
  depositPercent?: number;
  depositFixedYenPerPerson?: number;
  noShowProtectionEnabled?: boolean;
  // #737: キャンセル料保証 ON 時、8 日以上先の予約 (= Stripe 与信期限超え) をどう扱うか。
  // Phase 1 は UI のみで、実際の挙動は Phase 2 (#738) で実装。
  noShowProtectionLongTermFallback?: 'none' | 'deposit' | 'prepaid';
  // Note: responseDeadlineHours / cancelDeadlineHours are platform-wide constants
  // (see services/pay/frontend/src/pages/StoreDetail/reservationPolicy.ts) — not per-store,
  // to keep customer-facing policy consistent across stores.
}

export interface Reservation {
  id: string;
  storeId: string;
  customerId: string;
  date: string;   // YYYY-MM-DD
  time: string;   // HH:MM
  partySize: number;
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired' | 'visited' | 'no_show';
  // #1373 サーバは 'customer' | 'store' | 'system' を書く (reservations.py)。
  // 'owner' は予約側では一度も書かれておらず、逆に実在する 'store' が型に無かったため
  // i18n キーも用意されず、履歴に `myReservations.cancelledBy.store` が生で出ていた。
  // ('owner' は Order 型の方で実際に使われているので、そちらは変えない)
  cancelledBy?: 'customer' | 'store' | 'system';
  // #1375 返金の実行結果。**規定から計算した額ではなく実際に返金できた額**を持つ。
  // 失敗したまま放置すると「客に返していない金を店が持っている」状態が見えない。
  // #1374 の売上計上も「預かり額 − refundedAmount」で決まるためここが真実になる。
  // #2226 'alternate' = カードに返せず、お店が別の方法 (現金・銀行振込など) で返した (解消済み)
  refundStatus?: 'succeeded' | 'pending' | 'failed' | 'none' | 'alternate';
  refundedAmount?: number;
  /** #2226 お店が別の方法で返した記録。amount は記録した時点の未返金 */
  refundAlternate?: { method: 'cash' | 'bank_transfer' | 'other'; amount: number; note?: string | null; recordedAt: string };
  /** #2226 カードに返せなかったことをお客様に知らせた記録 (channel 'none' = メールが無く知らせられない) */
  refundUnreachableNotice?: { notifiedAt: string | null; channel: 'email' | 'none' | 'withdrawn'; voucherId?: string };
  /** #2226 今の返金の引換券の id (コードではない。コードは本人の画面にだけ出る) */
  refundVoucherId?: string;
  refundIntendedAmount?: number;
  // #2018 実際に預かっている額 (承認待ちの増員は含まない)。返金の表示の上限
  paidAmount?: number;
  refundedAt?: string;
  refundError?: string;
  // #2476 カードに返せなかった返金にチャージバックが来ている (サーバの判断。カード会社から戻る)
  refundDisputed?: boolean;
  cancelledAt?: string;
  reasonCode?: string;
  slotClosed?: boolean;
  // #737 course reservation extension
  courseId?: string;
  courseName?: string;
  /** #1644 予約時のコース品目スナップショット (改行区切り)。予約履歴で内容を見せる */
  courseDishList?: string;
  /** #1644 選択した飲み放題プラン (予約時スナップショット)。null/undefined = なし */
  drinkPlanName?: string | null;
  drinkPlanPriceYen?: number | null;
  totalAmount?: number;
  paymentTiming?: 'prepaid' | 'deposit' | 'onsite';
  // #745 Phase 2: 'partially_refunded' は人数/コース変更で部分返金が走った後の状態
  paymentStatus?: 'pending' | 'authorized' | 'paid' | 'refunded' | 'partially_refunded' | 'forfeited' | null;
  depositAmount?: number | null;
  // #738 Phase 2: payment_timing_resolver の出力スナップショット
  longTermFallbackApplied?: boolean;
  captureMethod?: 'automatic' | 'manual' | null;
  paymentIntentId?: string | null;  // Stripe PaymentIntent ID (Phase 2 で書き込まれる)
  // #1804 ポイントで払った予約は 'points' (改名前の 'jpyc' も。比べるときは lib/paymentMethods の isPointsMethod)
  paymentMethod?: string;
  stripeAccountId?: string | null;  // Direct Charge 加盟店 ID (PI 作成時)
  // #746 公開 share URL `/r/{shortCode}` 用の 8 桁 base62 code (予約作成時に生成)
  shortCode?: string;
  // #745 Phase 2.1: 人数変更時に増分決済で発行された PI 一覧 (= 既存 paymentIntentId とは別管理)
  additionalPaymentIntentIds?: string[];
  // #745 Phase 2.1: 変更履歴 (= partySize / date / time / courseId 変更ログ)
  // 'changeRejected' = 店舗が変更の再承認を却下して元に戻した記録 / by: 'store' = 店舗が動かした
  // 'changeExpired' = 店舗が期限内に返答せず、変更を取り下げて元に戻した記録 (#1994) / by: 'system'
  changeHistory?: {
    type: 'partySize' | 'datetime' | 'course' | 'changeRejected' | 'changeExpired';
    from: number | string;
    to: number | string;
    reason?: string | null;
    at: string;
    by?: 'customer' | 'store' | 'system';
    changeType?: 'partySize' | 'datetime' | 'course';
  }[];
  // 承認済み予約への変更 (日時/人数/コース) の再承認待ち。null/undefined = 通常の pending。
  // 却下されると snapshot の内容へ巻き戻り approved に戻る (予約は消えない・金は動かない)
  pendingChange?: {
    type: 'partySize' | 'datetime' | 'course';
    from: number | string;
    to: number | string;
    snapshot: { date?: string; time?: string; partySize?: number; totalAmount?: number | null; depositAmount?: number | null; courseId?: string | null; courseName?: string | null };
    at: string;
  } | null;
  // 却下時に店舗がお客様へ残したメッセージ (「この日なら空いています」等)
  storeMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

// #737 Course — 食べログ準拠の店舗別コース予約データモデル。
// stores/{storeId}/courses/{courseId} のサブコレクション。
/** 表示専用の宣伝枠 (自由テキスト)。注文・課金には繋がらない。
 * 予約時に選べる飲み放題は Course.drinkPlanIds (実体プラン参照) で表現する */
export interface CourseDrinkPlan {
  name: string;
  priceYen: number;
  description?: string;
}

export interface CourseCampaign {
  title: string;
  description: string;
  expiresAt?: string | null;
}

export interface CourseSelectablePerk {
  label: string;
  description?: string;
}

export interface Course {
  id: string;
  storeId?: string;
  name: string;
  photoUrls: string[];
  description: string;
  priceYen: number;
  numberOfDishes?: number | null;
  partySizeRange: { min: number; max: number };
  durationMinutes: number;
  availableDays: number[];          // 0-6 (Sun-Sat)
  availableTimeSlots: string[];     // ["18:00", "19:00", ...]
  dishList: string;                 // ◆-prefixed, newline-separated
  /** 表示専用の宣伝枠（名前と金額だけ）。注文には繋がらない。 */
  drinkPlans?: CourseDrinkPlan[];
  /** #1644 予約時に有料オプションとして選べる実体プラン (#1353) の id。
   * 名称・価格・説明は実体プランが SSOT (コース側で複製しない) */
  drinkPlanIds?: string[];
  /**
   * #1353「コース + 飲み放題」の紐付け。これがあると来店マーク時に卓へ
   * 放題プランが自動適用され、含まれるドリンクが 0 円で注文できる。
   */
  allYouCanPlanId?: string | null;
  // #1357 当日その場で注文できるか。既定（未設定）は要予約。
  orderableOnSite?: boolean;
  // 何日前までに予約が必要か。0 = 当日予約可。
  reservationDeadlineDays?: number;
  campaign?: CourseCampaign | null;
  selectablePerks?: CourseSelectablePerk[];
  cancellationNote?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * #1353 放題プラン（飲み放題 / 食べ放題）。
 *
 * 商品を 0 円で登録するのではなく、既存の単品商品を `itemIds` で束ね、
 * プラン適用中の卓でだけ 0 円で注文できる（Airレジと同じ型）。
 */
/** #2039 バインダーの種別 (番号の範囲 → 付けるプラン) */
export interface BinderType {
  id: string;
  name: string;
  start: number;
  end: number;
  planId?: string | null;
  allFree?: boolean;
}

/** #2039 時間制の段。経過が `afterMinutes` 以上で始まる単位から、この単価になる。 */
export interface PlanTier {
  afterMinutes: number;
  priceYen: number;
}

/** #2052 時間帯の単価。`startTime` ("HH:MM") から、営業日の中で次の時間帯が始まるまで */
export interface PlanBand {
  startTime: string;
  priceYen: number;
  /** 週末の単価。無ければこの行の `priceYen` を使う */
  weekendPriceYen?: number | null;
}

export interface AllYouCanPlan {
  id: string;
  name: string;
  /** flat: 1 名あたりの料金 / time: 1 名・1 単位あたりの料金 (最初の段)。どちらも税込 */
  priceYen: number;
  /** flat: 制限時間 / time: 上限 (ここで課金が頭打ちになる。閉め忘れ対策) */
  durationMinutes: number;
  noticeBeforeMinutes: number; // 事前通知（終了何分前にラストオーダー）
  itemIds: string[];           // 含まれるメニュー item の id
  description?: string;
  isActive: boolean;
  /** #2039 料金タイプ。未設定 = 'flat' (今までの放題プラン)。'time' = 時間で課金 */
  pricing?: 'flat' | 'time';
  unitMinutes?: number;
  tiers?: PlanTier[];
  /** #2052 曜日・時間帯の単価 (`tiers` とは同時に持てない)。定義は services/time_charge.py */
  bands?: PlanBand[];
  /** 週末料金にする曜日 (0 = 月 … 6 = 日)。**営業日**の曜日で見る */
  weekendDays?: number[];
  /** 週末の基本の単価 */
  weekendPriceYen?: number | null;
}

/** #2039 時間料金の今の状態。金額の定義はサーバ (services/time_charge.py) が正 */
export interface TimeChargeState {
  unitMinutes: number;
  unitCount: number;
  amountYen: number;
  chargedPartySize: number;
  freePartySize: number;
  /** 時計が動いているか。会計開始で止まり、止まっている間は金額が変わらない */
  running: boolean;
  stoppedAt: string | null;
  /** 次の単位に入る時刻。止まっている / 上限に達していれば null */
  nextUnitAt: string | null;
  /** この適用の伝票が支払い済みか (止まっているときだけ返る)。払い終わった卓は訂正できない */
  settled?: boolean;
  lines: { unitPriceYen: number; unitCount: number }[];
}

/** 卓に適用中の放題プランの状態（残り時間つき） */
export interface AllYouCanState {
  planId: string;
  name: string;
  partySize: number;
  /** 1名あたりの料金。人数変更時の金額確認に使う */
  priceYen: number;
  /** 'standalone' = 単品で計上 / 'course' = コース料金に含まれ非計上 */
  billing: string;
  startedAt: string;
  endsAt: string;
  lastOrderAt: string;
  remainingMinutes: number;
  active: boolean;
  lastOrderPassed: boolean;
  itemIds: string[];
  /** #2039 'time' のとき `timeCharge` に現在額・次の単位の時刻が入る */
  pricing?: 'flat' | 'time';
  timeCharge?: TimeChargeState | null;
}

// Self-order (#289) — only meaningful when VITE_SELF_ORDER_ENABLED is true.
export interface CartItem {
  menuItemId: string;
  name: string;
  nameEn: string;
  /** #1857 客の言語での品目名。注文前はここ (メニューの訳文から解決)、
   *  注文後はサーバが焼き込んだ値。日本語で見ている間は undefined。 */
  nameLocalized?: string;
  price: number;
  imageUrl?: string;
  quantity: number;
  // #1353 放題プランで 0 円になった明細。サーバが注文時に立てる
  // (`apply_to_items`)。#1585 で「もう提供済みか」の判定にも使う。
  allYouCan?: boolean;
  /** 0 円になる前の通常価格。レシート/分析で「本来いくらの品か」を追うため。 */
  listPrice?: number;
  /** #1644 コース明細の品目 (予約時スナップショット、改行区切り)。
   * 注文履歴で「何が出てくるか」を見せる。コース予約の起票時のみ入る */
  dishList?: string;
  /** #2031 カートの行の鍵。**同じ商品でも選んだ内容が違えば別の行**になるので、
   *  行は商品 ID ではなくこれで指す。選んでいない商品では商品 ID と同じ (省略可)。 */
  lineId?: string;
  /** #2031 客が選んだバリエーション (注文前)。サーバへはこの形で送り、サーバが検算する。 */
  selectedVariations?: VariationSelection;
  /** #2031 注文前の表示用 (`味: たれ`)。カートに入れた時点の店の定義から起こす。 */
  variationText?: string;
  /** #2031 サーバが検算して焼き込んだ選択 (注文後)。表示はこちらを使う。 */
  variations?: VariationSnapshot[];
  /** #2039 時間料金の行 (サーバが起こす)。客が頼んだ品ではないので、再注文の対象にしない */
  timeCharge?: boolean;
  /** #2031 注文の行を指す鍵 (サーバ採番)。品目取消で「たれの行だけ」を指すのに使う。 */
  lineKey?: string;
  /** #2031 有料の選択 (味玉 +100 円) の合計 (1 個あたり)。**`price` に含まれている** —
   *  カートでも注文後でも同じ意味。送るときだけ本体へ戻す (lib/cartPayload.ts)。 */
  optionsPrice?: number;
}

export interface Cart {
  storeId: string;
  storeName: string;
  items: CartItem[];
  tableNumber?: string;
  // #284: delivery context. Defaults to 'dine-in' (店内/持ち帰り the existing
  // self-order flow). When the customer enters via the /delivery flow the
  // cart is flagged 'delivery' and the checkout collects an address.
  // #1213 'takeout' = 先払いして店頭カウンターで受け取る (卓・住所とも不要)
  orderType?: 'dine-in' | 'delivery' | 'takeout';
  deliveryAddress?: string;
  deliveryNotes?: string;
  // Geolocation captured when the customer opened /delivery/stores. The
  // store side shows a "地図で開く" link that uses these coords so the
  // delivery runner can navigate even when the free-text address is a
  // landmark name (hotel lobby, park entrance, etc.).
  deliveryLat?: number;
  deliveryLng?: number;
}

/**
 * #2190 アプリのカード会計の確定の結果 (backend の「アプリのカード会計の確定」)。確定は何度呼んでも 1 回分。
 * `duplicate_refunded` は、先にほかの支払いで払われていた会計 — この決済はカードに返金する。
 */
export interface CheckoutConfirmResult {
  outcome?: 'settled' | 'already' | 'duplicate_refunded'
  transactionId?: string
  refundedAmount?: number
}

export interface Order {
  id: string;
  storeId: string;
  storeName?: string;  // 注文時の店名の控え (一覧に出ない店でも客の画面に店名を出すため)
  customerId: string;
  customerName?: string;  // snapshot for store-side identification (#289)
  // #977 注文者の識別。'staff' = 店員の代理入力、'customer'（既定）= 顧客アプリ。
  placedBy?: 'customer' | 'staff';
  // #1400 店員入力の入力者名（作成時スナップショット）。店舗側画面にのみ出す。
  placedByStaffName?: string;
  items: CartItem[];
  totalAmount: number;
  // Kitchen / fulfilment lifecycle. `served` is the terminal state for
  // dine-in; `delivered` is the terminal state for delivery (#284). The
  // extra delivery-only states are only valid when `orderType === 'delivery'`
  // — enforced server-side in PATCH /orders/{id}.
  // #947 (管理番号_004) 'pending_approval' is the initial state of a dine-in
  // table's first order: it waits for staff approval before entering the
  // kitchen (received). Approving opens the table; rejecting cancels it.
  // #1213 takeout は `ready` (受け取り可能=呼び出し対象) → `picked_up` (受け渡し完了)。
  // dine-in の `served` (席まで運んだ) とは運用上別物なので状態を分けている。
  status: 'pending_approval' | 'received' | 'preparing' | 'served' | 'ready' | 'picked_up' | 'out_for_delivery' | 'delivered' | 'cancelled';
  paid: boolean;
  // #1214 呼び出し番号 (例 `A-01`)。店舗別・日次で採番し、厨房伝票/レシート/
  // 客画面で同じ番号を出す。未採番の既存注文は doc id 先頭6文字に fallback
  // (lib/orderNumber.ts の displayOrderNumber)。
  callNumber?: string;
  // #1216 受け取り案内 (「準備できました」) をサーバ側で表示済みにした時刻。
  // localStorage と違いキャッシュ削除・機種変更でも消えないので、過去分が
  // 一斉に再通知されない。
  readyNoticeShownAt?: string;
  // #1216 `ready` になった時刻 (時間窓の起点)。
  readyAt?: string;
  // #947 set when the owner approves a pending_approval order.
  approvedBy?: string;
  approvedAt?: string;
  // #941/#942 現金会計: 'cash' orders settle at the register instead of via
  // Stripe. cashPending=true means the customer chose cash and is waiting
  // for the store to confirm receipt (paid flips true on owner approval).
  paymentMethod?: 'stripe' | 'cash' | 'store_terminal';  // #2162 お店の決済端末で受けた会計
  cashPending?: boolean;
  cashApprovedAt?: string;
  cashApprovedBy?: string;
  // #956 Phase 2 卓単位一括会計: set when a representative customer settles the
  // whole table by cash — identifies who is paying so /approve-cash-table
  // attributes the single cash transaction to them. Its presence also tells the
  // store board this is a table bill (one 一括現金受領 button) vs per-customer.
  cashPayerId?: string;
  tableNumber?: string;
  // #968 セルフオーダー入店時に客が入力した来店人数（卓セッションから複製）。店員ボード表示用。
  partySize?: number;
  notes?: string;
  paymentIntentId?: string;
  // #1589 同じ会計で払われた注文を束ねるキー (= 台帳 transactions の doc id)。
  // カードは paymentIntentId と同値、現金は `cash_*`。**現金にはこれしか無い**
  // (paymentIntentId はカード限定) ので、会計単位で見るときは必ずこちらを使う。
  settlementId?: string;
  // #2344 その会計で実際に受け取った額。注文の合計とは別物で、お通し・席料が
  // 足され、値引きが引かれている。会計に 1 つの値なので、同じ settlementId の
  // 注文には同じ額が入る (足し合わせると会計の数だけ重複する)。
  settlementTotal?: number;
  paidAt?: string;
  // #1393 店員が「退店」を押して卓を空けた時刻。支払い済みでもこれが無い間は
  // **退店待ち**として店舗ボードに残す（決済で卓が消えると、店員は会計されたのか
  // まだ客が座っているのか判断できない）。
  tableClearedAt?: string;
  // #314 task #5: customer pressed "届いていない" on a served order. The
  // owner-side card highlights this for follow-up (remake or void).
  missingReported?: boolean;
  // Set by POST /orders/{id}/remake on the new clone, points back at the
  // cancelled original so the customer's history is traceable.
  remadeFromOrderId?: string;
  // #284 出前: orderType distinguishes the fulfilment channel. When
  // 'delivery' the customer is required to provide deliveryAddress and the
  // order is pre-paid at checkout (Stripe PaymentIntent completed upfront).
  // #1213 'takeout' = 先払いして店頭カウンターで受け取る (卓・住所とも不要)
  orderType?: 'dine-in' | 'delivery' | 'takeout';
  deliveryAddress?: string;
  deliveryNotes?: string;
  // Geolocation snapshot — surfaced as a "地図で開く" link on the store
  // side so delivery staff can navigate regardless of how precisely the
  // customer wrote the address.
  deliveryLat?: number;
  deliveryLng?: number;
  // Pre-auth vs captured state for delivery (manual-capture). Captured
  // flips to true when the owner moves the order to 'preparing'. Dine-in
  // orders leave these unset (they capture at checkout time).
  captured?: boolean;
  // Cancellation metadata — surfaced to the customer so they know why
  // their order was cancelled and whether a refund was issued.
  cancelledBy?: 'customer' | 'owner' | 'system';
  cancelReason?: string;
  // #338 in-order chat unread counters. Customer-side badge reads
  // unreadCountForCustomer; owner-side reads unreadCountForOwner. Reset to
  // 0 when the reader opens the chat sheet.
  unreadCountForCustomer?: number;
  unreadCountForOwner?: number;
  createdAt: string;
  updatedAt: string;
}

// #1188 Phase 2: Stripe customer_balance PaymentIntent の next_action.
// display_bank_transfer_instructions を frontend が受ける形。日本の Zengin
// 口座情報を表示する ChargeBank / ChargeRevolut 画面が使う。
export interface ZenginAccount {
  bank_code: string;
  bank_name: string;
  branch_code: string;
  branch_name: string;
  account_type: 'futsu' | 'toza';
  account_number: string;
  account_holder_name: string;
}
export interface BankTransferInstructions {
  charge_id: string;
  amount_jpy: number;
  fee_jpy: number;
  total_jpy: number;
  payment_intent_id?: string;
  funding_instructions?: {
    type: 'jp_bank_transfer';
    currency: 'jpy';
    amount_remaining: number;
    hosted_instructions_url?: string;
    reference?: string | null;
    financial_addresses: Array<{
      type: 'zengin';
      zengin?: ZenginAccount;
    }>;
  };
}

export interface Transaction {
  /** #2476 カードに返せなかった返金にチャージバックが来ている (サーバの判断。カード会社から戻る) */
  refundDisputed?: boolean;
  id: string;
  type: 'charge' | 'payment' | 'adjustment';
  // #2162 store_terminal = お店の決済端末 (STORES 決済・Airペイなど) で受けた会計の記録
  // 'points' = ポイント払い。'jpyc' は改名前のポイント払い (#2571。暗号資産の JPYC とは関係ない)
  paymentMethod?: 'points' | 'jpyc' | 'stripe' | 'cash' | 'store_terminal';
  amount: number;
  fee?: number;
  storeId?: string;
  storeName?: string;
  /**
   * #1661 取引時点の店の業態。**これを見ること。**
   *
   * storeCategory は正規化前のデータ用に残してあるだけで、新しい取引では
   * 業態が入らない (飲食の細分類しか入らない)。
   */
  storeGenre?: 'food' | 'souvenir' | 'experience' | 'other';
  /** @deprecated #1661 業態は storeGenre を見る。古い取引の判定用にのみ残す。 */
  storeCategory?: string;
  timestamp: Date;
  status: 'completed' | 'pending' | 'failed' | 'refunded' | 'partially_refunded';
  txHash?: string;
  explorerLink?: string;
  // #1764 チャージの経路 (履歴に「銀行振込 / Revolut / カード / Alipay・WeChat」を出す)
  chargeMethod?: 'card' | 'bank_transfer' | 'revolut' | 'alipay' | 'wechat_pay' | 'local_payment';
  // backend が本当にチェーン上で処理した取引 (TestJPYC の mint / 送金) だけ true。
  // それ以外の txHash はレシート用の乱数で、チェーン上には何も無い
  onChain?: boolean;
  refund?: {
    amount: number;
    reason: 'customer' | 'store';
    method: 'stripe' | 'cash';
    fee: number;
    stripeRefundId: string;
    refundedAt: string;
    // #533: Stripe refund status. /store-auth/refund 時の API レスポンスを
    // 初期値、refund.updated webhook で 'succeeded' / 'failed' に遷移。
    // legacy data (本フィールド導入前の refund) では undefined。
    stripeRefundStatus?: 'pending' | 'requires_action' | 'succeeded' | 'failed' | 'canceled';
    stripeRefundFailureReason?: string;
    // #533: カード返金が後段 failed/canceled になった場合に店舗が代替手段
    // (現金/銀行振込/その他) で顧客に返金した記録。記録されている = 顧客
    // 視点では返金完了。
    altRefund?: {
      method: 'cash' | 'bank_transfer' | 'other';
      note?: string;
      completedAt: string;  // ISO 8601 JST
      byUid: string;
    };
  };
  // Expense tracking (business / personal)
  expenseTag?: 'business' | 'personal';
  expenseCategory?: string;  // locale-independent category key e.g. 'meals'
  expenseMemo?: string;
  // #1207 品目明細スナップショット (卓会計/物販由来の tx のみ。QR 手入力は無し)
  items?: Array<{
    name?: string; nameEn?: string; price?: number; quantity?: number;
    /** #2031 選んだバリエーション (取引に焼き込んだもの) */
    variations?: VariationSnapshot[] | null;
  }>;
  // #1397 この取引で締められた注文 ID。現金会計はサーバ側で取引が作られるため、
  // client はこれを手掛かりに「今回の会計の取引」を特定する。
  orderIds?: string[];
}

export interface CreditCard {
  id: string;
  brand: 'visa' | 'mastercard' | 'amex';
  last4: string;
  expiry: string;
  name: string;
  isDefault: boolean;
}

export interface StoreStats {
  visitors: number;
  revenue: number;
  countries: { flag: string; name: string; count: number; percentage: number }[];
}

// Constants
export const EXCHANGE_RATES = {
  USD_JPY: 149.50,
  EUR_JPY: 162.30,
  TWD_JPY: 4.65,
  KRW_JPY: 0.11,
  CNY_JPY: 20.50,
};

export const DAILY_CHARGE_LIMIT = 100000;

// Fee configuration — all rates are overridable via environment variables
// so that switching partners (Alchemy Pay → JCB/DG etc.) requires no code changes
//
// 実証実験フェーズ: デフォルト 0%（手数料なし）
// 本番移行時の値: fixed=350, rate=0.044, markup=0.008, payment=0.018
export const FEE_CONFIG = {
  // Charge fee (user pays) — shown in mock mode; Alchemy Pay handles its own in production
  charge: {
    fixed: Number(import.meta.env.VITE_CHARGE_FEE_FIXED) || 0,
    rate: Number(import.meta.env.VITE_CHARGE_FEE_RATE) || 0,
    markup: Number(import.meta.env.VITE_CHARGE_MARKUP_RATE) || 0,
    calculate: (amount: number) => {
      const fixed = Number(import.meta.env.VITE_CHARGE_FEE_FIXED) || 0;
      const rate = Number(import.meta.env.VITE_CHARGE_FEE_RATE) || 0;
      const fee = fixed + Math.floor(amount * rate);
      return { fee, total: amount + fee, netAmount: amount };
    },
  },
  // Payment fee (store pays) — deducted from payment amount
  payment: {
    rate: Number(import.meta.env.VITE_PAYMENT_FEE_RATE) || 0,
    calculate: (amount: number) => {
      const rate = Number(import.meta.env.VITE_PAYMENT_FEE_RATE) || 0;
      const fee = Math.floor(amount * rate);
      return { fee, storeAmount: amount - fee };
    },
  },
};

// #711: 顧客検索 list の表示判定。/stores/nearby は backend 側で同等 filter
// (discoverability='public' && operability='open' + KYC chargesEnabled) を
// かけているので、本関数は防御的な二重 check + ローカル state からの list
// 描画用 (例: 店舗追加直後の楽観 update 等)。env var 由来の bypass は #711 で
// 廃止 (旧 VITE_SHOW_TEST_STORES)。
export function filterVisibleStores(stores: Store[]): Store[] {
  return stores.filter(
    (s) =>
      (s.discoverability ?? 'public') === 'public' &&
      (s.operability ?? 'open') === 'open',
  );
}

function toBalanceUSD(jpyc: number): number {
  return +(jpyc / EXCHANGE_RATES.USD_JPY).toFixed(2);
}

function getUserId(): string {
  return localStorage.getItem('userId') || 'demo_user_001';
}

// Convert API transaction format to frontend format
function loadDietaryFromStorage(): { allergens: string[]; styles: string[] } {
  try {
    return {
      allergens: JSON.parse(localStorage.getItem('dietaryAllergens') || '[]'),
      styles: JSON.parse(localStorage.getItem('dietaryStyles') || '[]'),
    };
  } catch {
    return { allergens: [], styles: [] };
  }
}

function mapTransaction(tx: Record<string, unknown>): Transaction {
  return {
    id: (tx.id as string) || '',
    type: (tx.type as 'charge' | 'payment' | 'adjustment') || 'charge',
    amount: (tx.amountJpyc as number) || 0,
    fee: (tx.feeJpyc as number) || 0,
    storeId: (tx.storeId as string) || undefined,
    storeName: (tx.storeName as string) || undefined,
    storeGenre: (tx.storeGenre as Transaction['storeGenre']) || undefined,
    storeCategory: (tx.storeCategory as string) || undefined,
    timestamp: tx.createdAt ? new Date(tx.createdAt as string) : new Date(),
    status: (tx.status as Transaction['status']) || 'completed',
    paymentMethod: (tx.paymentMethod as 'points' | 'jpyc' | 'stripe' | 'cash' | 'store_terminal') || undefined,
    refund: tx.refund as Transaction['refund'],
    txHash: (tx.txHash as string) || undefined,
    explorerLink: tx.txHash ? getExplorerTxLink(tx.txHash as string) : undefined,
    chargeMethod: (tx.chargeMethod as Transaction['chargeMethod']) || undefined,
    onChain: tx.onChain === true,
    expenseTag: (tx.expenseTag as 'business' | 'personal') || undefined,
    expenseCategory: (tx.expenseCategory as string) || undefined,
    expenseMemo: (tx.expenseMemo as string) || undefined,
    items: (tx.items as Transaction['items']) || undefined,
    orderIds: Array.isArray(tx.orderIds) ? (tx.orderIds as string[]) : undefined,
  };
}

// Convert API card format to frontend format
function mapCard(card: Record<string, unknown>): CreditCard {
  return {
    id: (card.id as string) || '',
    brand: (card.brand as CreditCard['brand']) || 'visa',
    last4: (card.last4 as string) || '',
    expiry: (card.expiry as string) || '',
    name: (card.name as string) || '',
    isDefault: (card.isDefault as boolean) || false,
  };
}

// Convert API favorite format to frontend format
function mapFavorite(f: Record<string, unknown>): FavoriteStore {
  return {
    storeId: (f.storeId as string) || '',
    name: (f.name as string) || '',
    nameEn: (f.nameEn as string) || '',
    imageUrl: (f.imageUrl as string) || '',
    category: (f.category as Store['category']) || undefined,
    genre: (f.genre as Store['genre']) || undefined,
  };
}

// Store
interface YokosoState {
  // User
  isLoggedIn: boolean;
  userId: string | null;
  user: User | null;
  wallet: Wallet | null;
  cards: CreditCard[];
  favoriteStores: FavoriteStore[];

  // Data
  stores: Store[];
  transactions: Transaction[];
  currentStore: Store | null;
  lastPaymentAmount: number;
  lastPaymentFee: number;
  lastPaymentTxHash: string | null;
  lastPaymentExplorerLink: string | null;
  // 直前の支払い (画面の状態。保存しない)。ポイント払い / Web3 の実験の JPYC (本物のトークン) / カード / 現金
  lastPaymentMethod: typeof PAYMENT_METHOD_POINTS | typeof WEB3_JPYC_PAYMENT | 'stripe' | 'cash' | null;
  // 直前の会計で作られた取引の ID。/payment/complete のレシート導線が「どの取引か」を
  // これで確定する。transactions 配列の並びはサーバ由来 (createdAt DESC) とローカル
  // 追記 (末尾) が混在するため、位置から推測すると別の取引を掴む (#1397)。
  lastPaymentTxId: string | null;
  // 現金会計は店舗承認でサーバ側に取引が作られるため client は ID を知らない。
  // 代わりに締められた注文 ID を控え、再取得した取引の orderIds と突き合わせる。
  lastPaymentOrderIds: string[];
  lastChargeAmount: number;
  lastChargeTxHash: string | null;
  lastChargeExplorerLink: string | null;

  // Store stats (for store dashboard)
  storeStats: StoreStats;

  // Travel budget
  budgetEnabled: boolean;
  budgetAmount: number;
  budgetStartedAt: number;
  budgetNotified80: boolean;
  budgetNotified100: boolean;

  // Pending wallet address (stored during login, applied during createWallet)
  pendingWalletAddress: string | null;

  // Cooldown: skip chain balance sync for 60s after a tx to avoid stale on-chain data
  lastTxTimestamp: number;

  // Actions
  login: (web3AuthData?: { name: string; email: string; walletAddress?: string }) => Promise<{ isNewUser: boolean }>;
  createWallet: () => void;
  restoreSession: () => Promise<void>;
  // 残高と取引だけをサーバから取り直す (失敗してもログアウトしない)
  refreshBalance: () => Promise<void>;
  // #942 現金会計: pull the latest transactions from the backend. Cash
  // payments are confirmed on the store's device, so the customer's local
  // state has no optimistic record — /history refreshes via this on mount.
  refreshTransactions: () => Promise<void>;
  logout: () => void;
  deleteAccount: () => Promise<void>;
  setBalance: (amount: number) => void;
  charge: (amount: number) => Promise<void>;
  // #1188 Phase 2: 日本の銀行振込 / Revolut 経由チャージ。Stripe customer_balance
  // で JP バーチャル口座 (Zengin 形式) を発行して frontend に返す。
  // 客が銀行振込 → Stripe が着金検知 → charge webhook 経由で残高反映 (非同期)。
  // このメソッド自体は balance 更新をせず、funding_instructions を返して呼び出し側
  // (`ChargeBank.tsx` / `ChargeRevolut.tsx`) が表示する。
  createBankTransferIntent: (amount: number, channel?: 'revolut') => Promise<BankTransferInstructions>;
  pay: (amount: number, store: Store) => Promise<void>;
  payWithStripe: (amount: number, store: Store, paymentIntentId: string) => Promise<void>;
  paySelfOrderCheckout: (amount: number, store: Store, paymentIntentId: string, orderIds: string[]) => Promise<CheckoutConfirmResult>;
  // #956 Phase 2 卓単位一括会計: confirm a table bill (orderIds span multiple
  // accounts). Mirrors paySelfOrderCheckout but hits the table-confirm endpoint.
  payTableCheckout: (amount: number, store: Store, paymentIntentId: string, orderIds: string[], tableNumber: string) => Promise<CheckoutConfirmResult>;
  // #968 現金会計は店舗承認で後から paid 化する非同期フロー。承認検知時にこの
  // メソッドで支払いコンテキスト(currentStore/金額/method='cash')をセットし、
  // /payment/complete（レシート＋レビュー＋シェア）にカードと同じく合流させる。
  markCashPaymentComplete: (store: Store, amount: number, orderIds?: string[]) => void;
  setCurrentStore: (store: Store | null) => void;
  refreshChainBalance: () => Promise<void>;
  addCard: (card: Omit<CreditCard, 'id'>) => void;
  removeCard: (id: string) => void;
  setDefaultCard: (id: string) => void;
  loadFavorites: () => Promise<void>;
  toggleFavorite: (store: Store) => void;
  setExpenseTag: (txId: string, tag: 'business' | 'personal' | null, category?: string, memo?: string) => void;
  setBudget: (enabled: boolean, amount: number) => void;
  resetBudget: () => void;
  getBudgetSpending: () => number;
  markBudgetNotified: (level: 80 | 100) => void;
  setDietaryPreferences: (allergens: string[], styles: string[]) => void;
  /** #2115 未ログインのまま選んだアレルギー・食事スタイル (端末に残す)。
   *  ログイン済みなら user 側が正。画面は user → こちら の順に見る。 */
  guestDietaryAllergens: string[];
  guestDietaryStyles: string[];
  loadStores: () => Promise<void>;
  /** 保存できたら true。失敗時は false を返し、楽観更新も巻き戻す (#1373)。 */
  updateStore: (storeId: string, updates: Partial<Store>) => Promise<boolean>;
  /**
   * API から読んだ値をローカルの stores に反映するだけ。**保存しない。**
   *
   * #1683: 店舗設定画面は GET の結果をローカルへ写すのに `updateStore` を
   * 使っており、開くたびに 15 フィールドを PUT で送り返していた。読んだ値を
   * 書き戻す構造なので、サーバが導出値を返す項目 (休会中の `operability`) が
   * あると**それがそのまま保存されて元の設定が消える**。読みと書きを分ける。
   */
  hydrateStore: (storeId: string, data: Partial<Store>) => void;
  /**
   * 売り切れトグルの結果を手元の store にも反映する (**API は呼ばない**)。
   *
   * 売り切れは専用エンドポイントで既に保存済み。ここで updateStore を呼ぶと
   * 店舗全体の更新 API を叩いてしまい、権限も範囲も違う。
   *
   * 画面ローカルの上書きを持つ代わりにこれを使う。上書きだと元データが古いままで、
   * どちらを見ているかが画面ごとに変わる (REQ-25)。
   */
  applyMenuItemSoldOut: (storeId: string, menuItemId: string, soldOut: boolean) => void;
  updateStoreRolePolicy: (storeId: string, rolePolicy: RolePolicy) => Promise<boolean>;
  addMenuItem: (storeId: string, item: MenuItem) => Promise<void>;
  /** #1910 保存できたら true。失敗したら楽観更新を巻き戻して false (例外は投げない。updateStore と同じ) */
  updateMenuItem: (storeId: string, itemId: string, updates: Partial<MenuItem>) => Promise<boolean>;
  deleteMenuItem: (storeId: string, itemId: string) => Promise<void>;
  resetState: () => void;

  // Self-order cart (#289) — one store at a time. Adding an item from a
  // different store replaces the cart.
  cart: Cart | null;
  addToCart: (store: Store, item: MenuItem, selection?: VariationSelection) => void;
  // #968 再注文: 既存注文の品をまとめてカートに再投入（着席中の同一店舗向け）。
  // 同店舗の dine-in カートがあれば数量をマージ、無ければ新規作成。
  reorderToCart: (store: Store, items: CartItem[], tableNumber?: string) => void;
  // #314 task #2: stamp the customer's cart with a table number when they
  // entered the store via a table QR. Persists across navigation so the
  // owner-side order card shows the table even after the customer browses
  // away and comes back.
  setCartTableNumber: (store: Store, tableNumber: string) => void;
  // #284 出前: stamp the cart with delivery mode + geolocation. lat/lng are
  // optional (fall back to address-only) but strongly preferred so the
  // store can ship a navigable map link.
  setCartDeliveryMode: (store: Store, coords?: { lat: number; lng: number }) => void;
  setCartDeliveryAddress: (address: string, notes?: string) => void;
  /** #1583 上限は品目で変わる (放題プランは人数なので店舗設定まで)。
   *  `maxQuantity` 未指定なら カートの storeId から解決する。 */
  // #2031 行は `lineId` で指す (`cartLineOf(item)`)。選んでいない商品では商品 ID と同じ。
  updateCartQuantity: (lineId: string, quantity: number, maxQuantity?: number) => void;
  removeFromCart: (lineId: string) => void;
  /** #2042 サーバが返した**今の本体の値段**でカートの行を直す (`price_changed`)。
   *  カートの price は 本体 + 追加 なので、追加分 (optionsPrice) は足し直す。 */
  applyCartPrices: (changes: { lineId: string; basePrice: number }[]) => void;
  clearCart: () => void;
}

/** #1857 カートに入れる時点の客の言語での名前。日本語で見ている間は持たせない
 *  (`name` と同じ値になるだけ)。注文後はサーバが menu から引き直して焼き込む。 */
/** #2031 カートの行の鍵。選んでいない商品では商品 ID と同じ (= 今までと同じ)。 */
export function cartLineOf(item: Pick<CartItem, 'menuItemId' | 'lineId'>): string {
  return item.lineId ?? item.menuItemId;
}

function localizedNameFor(item: MenuItem): string | undefined {
  // #2156 zh-TW を zh に潰さない (繁体字のお客様に簡体字が出ていた)
  const locale = normalizeLocale(i18n.language);
  if (locale === 'ja') return undefined;
  const name = selectLocalizedName(item, locale);
  return name && name !== item.name ? name : undefined;
}

export const useStore = create<YokosoState>((set, get) => ({
  isLoggedIn: false,
  userId: null,
  user: null,
  wallet: null,
  pendingWalletAddress: null,
  lastTxTimestamp: 0,
  cards: [],
  favoriteStores: [],
  stores: [],
  // #2115 ゲストの食事設定は端末から読み直す (リロードしても消えない)
  guestDietaryAllergens: loadDietaryFromStorage().allergens,
  guestDietaryStyles: loadDietaryFromStorage().styles,
  transactions: [],
  currentStore: null,
  lastPaymentAmount: 0,
  lastPaymentFee: 0,
  lastPaymentTxHash: null,
  lastPaymentExplorerLink: null,
  lastPaymentMethod: null,
  lastPaymentTxId: null,
  lastPaymentOrderIds: [],
  lastChargeAmount: 0,
  lastChargeTxHash: null,
  lastChargeExplorerLink: null,
  storeStats: {
    visitors: 0,
    revenue: 0,
    countries: [],
  },
  budgetEnabled: localStorage.getItem('budgetEnabled') === 'true',
  budgetAmount: Number(localStorage.getItem('budgetAmount')) || 30000,
  budgetStartedAt: Number(localStorage.getItem('budgetStartedAt')) || 0,
  budgetNotified80: localStorage.getItem('budgetNotified80') === 'true',
  budgetNotified100: localStorage.getItem('budgetNotified100') === 'true',

  // ============================================================
  // LOGIN: Creates user in Firestore, fetches balance from DB
  // ============================================================
  login: async (web3AuthData?: { name: string; email: string; walletAddress?: string }) => {
    const name = web3AuthData?.name || 'Alex Johnson';
    const email = web3AuthData?.email || 'alex@example.com';
    const walletAddr = web3AuthData?.walletAddress;
    const provider = walletAddr ? 'web3auth' : (web3AuthData?.email ? 'firebase' : 'demo');

    const result = await api.login(provider, name, email, walletAddr);
    const userId = result.user_id;
    const isNewUser = result.is_new_user ?? false;
    // #819 Track B-main Phase 1: 既知 device 記録 (best-effort・login を
    // ブロックしない)。Firebase 経路のみ成立 (demo/web3 は token 無で 401)。
    // #869c: Firebase MFA で TOTP 強要素を直前に通過した sign-in なら
    // `viaTotp=true` を渡し backend が auto-trust する(refined-(a) 相当)。
    void (async () => {
      let viaTotp = false
      try {
        const tokenResult = await auth.currentUser?.getIdTokenResult()
        if (tokenResult?.signInSecondFactor === 'totp') {
          viaTotp = true
        }
      } catch { /* best-effort */ }
      api.registerDevice(getDeviceId(), navigator.userAgent.slice(0, 80), viaTotp)
        .catch(() => {})
    })()
    localStorage.setItem('userId', userId);
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('sessionCreatedAt', String(Date.now()));

    // #1398 ゲストで作った注文をこのアカウントへ引き継ぐ。卓のゲスト注文は
    // 「カート」ではなく既に「注文」なので、食事の途中でログインすると
    // 会計がゲスト分とアカウント分に割れる。引き継げたらゲストIDは捨てる。
    // login をブロックしない (失敗しても注文はゲスト側に残るだけ)。
    // #1870 スタンプラリーも同じゲストIDで貯まるので**一緒に引き継ぐ**。
    // **両方終わってからでないとゲストIDを捨てられない** — 先に捨てると
    // もう片方が ID を失う。片方でも失敗したら捨てない (次のログインで再試行
    // できる。ログイン中は Bearer が優先されるので、残っていても害は無い)。
    const guestId = getGuestId();
    if (guestId) {
      void Promise.allSettled([
        api.claimGuestOrders(guestId),
        api.claimGuestStamps(guestId),
      ]).then((results) => {
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length === 0) {
          clearGuestId();
          return;
        }
        console.warn('[Auth] guest claim failed (keeping guest id for retry):', failed);
      });
    }
    console.log('[Auth] Login success:', { userId, balance: result.balance_jpyc, wallet: result.wallet_address, isNewUser });

    if (isNewUser) {
      // New user: create account only, wallet activates after profile setup
      const dietary = loadDietaryFromStorage();
      set({
        isLoggedIn: true,
        userId,
        user: {
          id: userId,
          name: result.name,
          email,
          avatar: '/avatars/alex.jpg',
          // #2005 新規登録の直後はまだ何も選んでいない。'US' を置くと
          // プロフィール登録の画面に「アメリカ」が入っているように見えるし、
          // 未回答のまま離脱した人がアメリカとして数えられる
          nationality: '',
          language: '',
          dietaryAllergens: dietary.allergens,
          dietaryStyles: dietary.styles,
        },
        wallet: null,
        pendingWalletAddress: result.wallet_address,
      });
    } else {
      // Returning user: restore full state from DB
      const session = await api.getSession();
      const dietary = loadDietaryFromStorage();
      set({
        isLoggedIn: true,
        userId,
        user: {
          id: userId,
          name: session.name,
          email: session.email,
          avatar: '/avatars/alex.jpg',
          // #2005 **`|| 'US'` を外す。** サーバが「未回答」として '' を返しているのに
          // ここで 'US' に戻していたら、何のために既定値をやめたのか分からなくなる
          nationality: session.nationality ?? '',
          language: session.language ?? '',
          dietaryAllergens: dietary.allergens,
          dietaryStyles: dietary.styles,
          nickname: session.nickname || undefined,
          displayPreference: session.display_preference || 'tag',
          wantsPaperReceipt: !!session.wants_paper_receipt,
          ageBand: session.age_band || '',
          gender: session.gender || '',
          residencePrefecture: session.residence_prefecture || '',
          residenceCity: session.residence_city || '',
          residenceMailOptOut: !!session.residence_mail_opt_out,
        },
        wallet: {
          address: session.wallet_address,
          balanceJPYC: session.balance_jpyc,
          balanceUSD: toBalanceUSD(session.balance_jpyc),
          chainBalanceJPYC: null,
        },
        transactions: (session.transactions || []).map(mapTransaction),
        cards: (session.cards || []).map(mapCard),
        pendingWalletAddress: null,
      });
      // お気に入りは別エンドポイントから hydrate（session を汚さない）。localStorage
      // の userId は上流(670行付近)で既に set 済みなので getUserId() が有効。
      void get().loadFavorites();
    }

    return { isNewUser };
  },

  // ============================================================
  // CREATE WALLET: Called after profile setup is complete
  // Wallet address already exists in Firestore (created during login)
  // This activates it in the frontend state
  // ============================================================
  createWallet: () => {
    const state = get();
    const address = state.pendingWalletAddress || '';
    set({
      wallet: {
        address,
        balanceJPYC: 0,
        balanceUSD: 0,
        chainBalanceJPYC: null,
      },
      pendingWalletAddress: null,
    });
    console.log('[Wallet] Wallet activated:', address);
  },

  // ============================================================
  // RESTORE SESSION: Fetch full state from DB
  // ============================================================
  restoreSession: async () => {
    const userId = getUserId();
    try {
      const session = await api.getSession();
      console.log('[Auth] Session restored from DB:', { userId, balance: session.balance_jpyc });
      const dietary = loadDietaryFromStorage();
      set({
        isLoggedIn: true,
        userId,
        user: {
          id: userId,
          name: session.name,
          email: session.email,
          avatar: '/avatars/alex.jpg',
          // #2005 **`|| 'US'` を外す。** サーバが「未回答」として '' を返しているのに
          // ここで 'US' に戻していたら、既定値をやめた意味が無い。
          // (ログイン直後の復元とアプリ再起動時の復元で 2 か所ある。両方直す)
          nationality: session.nationality ?? '',
          language: session.language ?? '',
          dietaryAllergens: dietary.allergens,
          dietaryStyles: dietary.styles,
          nickname: session.nickname || undefined,
          displayPreference: session.display_preference || 'tag',
          wantsPaperReceipt: !!session.wants_paper_receipt,
          ageBand: session.age_band || '',
          gender: session.gender || '',
          residencePrefecture: session.residence_prefecture || '',
          residenceCity: session.residence_city || '',
          residenceMailOptOut: !!session.residence_mail_opt_out,
        },
        wallet: {
          address: session.wallet_address,
          balanceJPYC: session.balance_jpyc,
          balanceUSD: toBalanceUSD(session.balance_jpyc),
          chainBalanceJPYC: null,
        },
        transactions: (session.transactions || []).map(mapTransaction),
        cards: (session.cards || []).map(mapCard),
      });
      void get().loadFavorites();
    } catch (err) {
      console.error('[Auth] Session restore failed:', err);
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userId');
      set({ isLoggedIn: false, userId: null, user: null, wallet: null });
    }
  },

  // #1764 残高だけ取り直す。restoreSession は失敗時にログアウトさせる (起動時の
  // 復元用) ので、チャージ反映後の更新のような「失敗しても困らない」場面では使わない。
  refreshBalance: async () => {
    const userId = getUserId();
    if (!userId) return;
    const session = await api.getSession();
    set((state) => ({
      wallet: {
        address: session.wallet_address,
        balanceJPYC: session.balance_jpyc,
        balanceUSD: toBalanceUSD(session.balance_jpyc),
        chainBalanceJPYC: state.wallet?.chainBalanceJPYC ?? null,
      },
      transactions: (session.transactions || []).map(mapTransaction),
    }));
  },

  // #942 現金会計: re-fetch transactions from the backend and replace local
  // state. Cash payments are recorded server-side when the store confirms
  // receipt, so unlike Stripe (which updates local state optimistically),
  // the customer only sees them after pulling fresh data. Called on /history
  // mount so the cash receipt shows up without a full session restore.
  refreshTransactions: async () => {
    // #863 id はサーバがトークンから引くので送らない。ここで見るのは
    // **未ログインなら叩かない**ため (401 を出して console を汚さない)。
    if (!getUserId()) return;
    try {
      const res = await api.getTransactions();
      set({ transactions: (res?.transactions || []).map(mapTransaction) });
    } catch (err) {
      console.error('[Transactions] refresh failed:', err);
    }
  },

  logout: () => {
    logoutWeb3Auth().catch(() => {});
    // #1542 **Firebase のサインイン状態も必ず切る**。
    //
    // Firebase Auth の既定の永続化は IndexedDB で、localStorage を消しても残る。
    // 一方 Pay / StampGate / ArGate / usePlanAuth は `onAuthStateChanged` で
    // 生きたセッションを見つけると自動で login() するため、**ログアウトしたのに
    // それらの画面に触れた瞬間に前のアカウントとして復活していた**。
    // ホテルのロビー端末など共有デバイスでは、次の客が前の客の履歴・予約・
    // ウォレットを見られる状態になる。
    //
    // await しない (呼び出し側は同期。失敗しても手元の状態は必ず消す)。
    signOut(auth).catch((e) => console.warn('[Auth] Firebase signOut failed:', e));
    clearLocalStoragePreservingDeviceId();
    set({
      isLoggedIn: false, userId: null, user: null, wallet: null,
      pendingWalletAddress: null, cards: [], favoriteStores: [], transactions: [],
      currentStore: null, lastPaymentAmount: 0, lastPaymentFee: 0,
      lastPaymentTxHash: null, lastPaymentExplorerLink: null, lastPaymentMethod: null,
      lastPaymentTxId: null, lastPaymentOrderIds: [],
      lastChargeAmount: 0, lastChargeTxHash: null, lastChargeExplorerLink: null,
    });
  },

  // ============================================================
  // DELETE ACCOUNT: Removes all user data from Firestore
  // ============================================================
  deleteAccount: async () => {
    try {
      const userId = getUserId();
      await api.deleteAccount(userId);
      console.log('[Auth] Account deleted from server:', userId);
    } catch (err) {
      console.error('[Auth] Account deletion failed:', err);
    }
    logoutWeb3Auth().catch(() => {});
    // #1542 退会でも Firebase のサインイン状態を切る。サーバ側のアカウントを
    // 消しても、セッションが残っていると自動再ログインが走ってしまう。
    signOut(auth).catch((e) => console.warn('[Auth] Firebase signOut failed:', e));
    clearLocalStoragePreservingDeviceId();
    set({
      isLoggedIn: false,
      userId: null,
      user: null,
      wallet: null,
      pendingWalletAddress: null,
      cards: [],
      favoriteStores: [],
      transactions: [],
      currentStore: null,
      lastPaymentAmount: 0,
      lastPaymentTxId: null,
      lastPaymentOrderIds: [],
      lastChargeAmount: 0,
    });
  },

  // Manual balance set (triple-tap debug feature)
  //
  // #1942 **サーバが返した額を使う。** 残高はロット経由で作るようになり、
  // キャンペーン還元など「デモが作っていないポイント」からは減らさないので、
  // 頼んだ額ちょうどにならないことがある。要求額を画面に入れたままにすると、
  // 次の取得で別の数字に飛ぶ。
  setBalance: (amount: number) => {
    const applyLocal = (value: number) =>
      set((state) => ({
        wallet: state.wallet
          ? { ...state.wallet, balanceJPYC: value, balanceUSD: toBalanceUSD(value) }
          : null,
      }));
    applyLocal(amount); // 先に出して待たせない (失敗時と確定時に入れ直す)
    api.resetBalance(getUserId(), amount)
      .then((res) => {
        const actual = typeof res?.new_balance === 'number' ? res.new_balance : amount;
        if (actual !== amount) applyLocal(actual);
        console.log('[DB] Balance synced:', actual);
      })
      .catch((err) => console.error('[DB] Balance sync failed:', err));
  },

  // ============================================================
  // CHARGE: DB is updated first, then local state synced
  // ============================================================
  // #1188 Phase 2: JP 銀行振込チャージ (customer_balance)。
  // 振込先 (Zengin) を返して呼び出し側が UI で表示、balance 更新は
  // charge webhook (backend) 経由で後から非同期反映される。
  createBankTransferIntent: async (amount: number, channel?: 'revolut') => {
    const intent = await api.createChargeIntent(amount, 'bank_transfer_jp', channel);
    return intent as BankTransferInstructions;
  },

  charge: async (amount: number) => {
    const feeInfo = FEE_CONFIG.charge.calculate(amount);
    const intent = await api.createChargeIntent(amount);
    const result = await api.executeCharge(intent.charge_id);

    const txHash = result.tx_hash && result.on_chain ? result.tx_hash : null;
    const explorerLink = result.explorer_link && result.on_chain ? result.explorer_link : null;
    console.log('[DB] Charge completed:', { amount, newBalance: result.new_balance, txHash, onChain: result.on_chain });

    // App balance always from Firestore; chain balance from blockchain
    let chainBalance: number | null = null;
    if (txHash) {
      const wallet = get().wallet;
      if (wallet?.address) {
        chainBalance = await getJpycBalance(wallet.address);
      }
    }

    set((state) => ({
      wallet: state.wallet
        ? {
            ...state.wallet,
            balanceJPYC: result.new_balance,
            balanceUSD: toBalanceUSD(result.new_balance),
            chainBalanceJPYC: chainBalance !== null ? chainBalance : state.wallet.chainBalanceJPYC,
          }
        : null,
      lastChargeAmount: amount,
      lastChargeTxHash: txHash,
      lastChargeExplorerLink: explorerLink,
      lastTxTimestamp: Date.now(),
      transactions: [
        ...state.transactions,
        {
          id: result.transaction_id,
          type: 'charge' as const,
          amount,
          fee: feeInfo.fee,
          txHash: txHash || undefined,
          explorerLink: explorerLink || undefined,
          timestamp: new Date(),
          status: 'completed' as const,
        },
      ],
    }));
  },

  // ============================================================
  // PAY: On-chain mode uses real JPYC transfer; demo mode uses Firestore
  // ============================================================
  pay: async (amount: number, store: Store) => {
    const userId = getUserId();
    const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true' || !isWeb3Mode();
    // #2355 follow-up **await する。** getEthersProvider は遅延読み込みのため async に
    // なった。await を落とすと `!!Promise` で**常に true**になり、オンチェーンで
    // 払えない人まで on-chain の経路に送る。既定モードは手前の条件で短絡するので、
    // ethers を取りに行くこともない
    const useOnChain =
      !isDemoMode && !!store.walletAddress && !!(await getEthersProvider());

    // Calculate payment fee (store pays this)
    const { fee: paymentFee, storeAmount } = FEE_CONFIG.payment.calculate(amount);

    if (!useOnChain) {
      // === DEMO MODE: existing Firestore-based payment ===
      const result = await api.executePayment(store.id, amount, userId);
      console.log('[DB] Payment completed (mock):', { store: store.name, amount, fee: paymentFee, storeReceives: storeAmount, newBalance: result.new_balance });

      set((state) => ({
        wallet: state.wallet
          ? { ...state.wallet, balanceJPYC: result.new_balance, balanceUSD: toBalanceUSD(result.new_balance) }
          : null,
        lastPaymentAmount: amount,
        lastPaymentFee: paymentFee,
        lastPaymentTxHash: null,
        lastPaymentExplorerLink: null,
        lastPaymentMethod: PAYMENT_METHOD_POINTS,
        lastPaymentTxId: result.transaction_id,
        lastPaymentOrderIds: [],
        lastTxTimestamp: Date.now(),
        currentStore: store,
        transactions: [
          ...state.transactions,
          {
            id: result.transaction_id,
            type: 'payment' as const,
            paymentMethod: PAYMENT_METHOD_POINTS,
            amount,
            fee: paymentFee,
            storeId: store.id,
            storeName: result.store_name,
            storeGenre: genreOf(store),
            storeCategory: store.category,
            timestamp: new Date(),
            status: 'completed' as const,
          },
        ],
      }));
      return;
    }

    // === ON-CHAIN MODE: Real JPYC transfer ===
    // Step 1: Transfer storeAmount (after fee) to store wallet on-chain
    const { txHash, explorerLink } = await transferJpyc(store.walletAddress!, storeAmount);

    // Step 2: Record the payment in backend (for stats/history only)
    const result = await api.recordPayment(store.id, amount, userId, txHash);

    // Step 3: Refresh on-chain balance
    const wallet = get().wallet;
    let newBalance: number | null = null;
    if (wallet?.address) {
      newBalance = await getJpycBalance(wallet.address);
    }

    console.log('[Chain] Payment completed:', { store: store.name, amount, fee: paymentFee, storeReceives: storeAmount, txHash, newBalance });

    // Step 4: Update local state
    set((state) => ({
      wallet: state.wallet
        ? {
            ...state.wallet,
            balanceJPYC: newBalance ?? (state.wallet.balanceJPYC - amount),
            balanceUSD: toBalanceUSD(newBalance ?? (state.wallet.balanceJPYC - amount)),
            chainBalanceJPYC: newBalance,
          }
        : null,
      lastPaymentAmount: amount,
      lastPaymentFee: paymentFee,
      lastPaymentTxHash: txHash,
      lastPaymentExplorerLink: explorerLink,
      lastPaymentMethod: WEB3_JPYC_PAYMENT,   // Web3 の実験の経路: 本物の JPYC トークン (ポイントではない)
      lastPaymentTxId: result.transaction_id,
      lastPaymentOrderIds: [],
      lastTxTimestamp: Date.now(),
      currentStore: store,
      transactions: [
        ...state.transactions,
        {
          id: result.transaction_id,
          type: 'payment' as const,
          amount,
          fee: paymentFee,
          storeId: store.id,
          storeName: result.store_name,
          storeGenre: genreOf(store),
          storeCategory: store.category,
          txHash,
          explorerLink,
          timestamp: new Date(),
          status: 'completed' as const,
        },
      ],
    }));
  },

  // ============================================================
  // PAY WITH STRIPE: Credit card payment via Stripe
  // YOKOSO PAY does not touch the money — Stripe handles it
  // ============================================================
  payWithStripe: async (amount: number, store: Store, paymentIntentId: string) => {
    const userId = getUserId();

    // Record in backend (for stats/history)
    const result = await api.confirmStripePayment(paymentIntentId, store.id, amount, userId);
    console.log('[Stripe] Payment recorded:', { store: store.name, amount, txId: result.transaction_id });

    set((state) => ({
      lastPaymentAmount: amount,
      lastPaymentFee: 0,
      lastPaymentTxHash: null,
      lastPaymentExplorerLink: null,
      lastPaymentMethod: 'stripe',
      lastPaymentTxId: result.transaction_id,
      lastPaymentOrderIds: [],
      lastTxTimestamp: Date.now(),
      currentStore: store,
      transactions: [
        ...state.transactions,
        {
          id: result.transaction_id,
          type: 'payment' as const,
          paymentMethod: 'stripe' as const,
          amount,
          fee: 0,
          storeId: store.id,
          storeName: result.store_name,
          storeGenre: genreOf(store),
          storeCategory: store.category,
          timestamp: new Date(),
          status: 'completed' as const,
        },
      ],
    }));
  },

  // Self-order bill checkout (#289 deferred-payment model).
  // Confirms with the backend, then mirrors payWithStripe's post-payment
  // state so PaymentComplete / Review reuse their existing logic for the
  // self-order flow too.
  payTableCheckout: async (amount: number, store: Store, paymentIntentId: string, orderIds: string[], tableNumber: string) => {
    const userId = getUserId();
    const result = await api.confirmTableCheckout(paymentIntentId, orderIds, store.id, tableNumber, userId);
    if (result?.detail) throw new Error(result.detail);
    // #2190 先にほかの支払いで払われていた: この決済は返金する。支払いとしては残さない
    if (result?.outcome === 'duplicate_refunded') return result as CheckoutConfirmResult;
    const txId: string = result?.transactionId || paymentIntentId;
    set((state) => ({
      lastPaymentAmount: amount,
      lastPaymentFee: 0,
      lastPaymentTxHash: null,
      lastPaymentExplorerLink: null,
      lastPaymentMethod: 'stripe',
      lastPaymentTxId: txId,
      lastPaymentOrderIds: orderIds,
      lastTxTimestamp: Date.now(),
      currentStore: store,
      transactions: [
        ...state.transactions,
        {
          id: txId,
          type: 'payment' as const,
          paymentMethod: 'stripe' as const,
          amount,
          fee: 0,
          storeId: store.id,
          storeName: store.name,
          storeGenre: genreOf(store),
          storeCategory: store.category,
          timestamp: new Date(),
          status: 'completed' as const,
          orderIds,
        },
      ],
    }));
    return result as CheckoutConfirmResult;
  },

  paySelfOrderCheckout: async (amount: number, store: Store, paymentIntentId: string, orderIds: string[]) => {
    const userId = getUserId();
    const result = await api.confirmSelfOrderCheckout(paymentIntentId, orderIds, userId);
    if (result?.detail) throw new Error(result.detail);
    // #2190 先にほかの支払いで払われていた: この決済は返金する。支払いとしては残さない
    if (result?.outcome === 'duplicate_refunded') return result as CheckoutConfirmResult;
    const txId: string = result?.transactionId || paymentIntentId;

    set((state) => ({
      lastPaymentAmount: amount,
      lastPaymentFee: 0,
      lastPaymentTxHash: null,
      lastPaymentExplorerLink: null,
      lastPaymentMethod: 'stripe',
      lastPaymentTxId: txId,
      lastPaymentOrderIds: orderIds,
      lastTxTimestamp: Date.now(),
      currentStore: store,
      transactions: [
        ...state.transactions,
        {
          id: txId,
          type: 'payment' as const,
          paymentMethod: 'stripe' as const,
          amount,
          fee: 0,
          storeId: store.id,
          storeName: store.name,
          storeGenre: genreOf(store),
          storeCategory: store.category,
          timestamp: new Date(),
          status: 'completed' as const,
          orderIds,
        },
      ],
    }));
    return result as CheckoutConfirmResult;
  },

  // Query on-chain JPYC balance for the current wallet address
  // If chain balance differs from Firestore, sync Firestore to match (e.g. after account re-creation)
  refreshChainBalance: async () => {
    if (!isWeb3Mode()) return;
    const wallet = get().wallet;
    if (!wallet?.address) return;

    try {
      const chainBalance = await getJpycBalance(wallet.address);
      const currentAppBalance = wallet.balanceJPYC;

      set((state) => ({
        wallet: state.wallet
          ? { ...state.wallet, chainBalanceJPYC: chainBalance }
          : null,
      }));

      // Sync Firestore if chain balance differs from app balance (records adjustment transaction)
      // Skip sync for 60s after a tx to avoid stale on-chain data triggering false adjustments
      const SYNC_COOLDOWN_MS = 60_000;
      const timeSinceLastTx = Date.now() - get().lastTxTimestamp;
      if (chainBalance !== null && chainBalance !== currentAppBalance && timeSinceLastTx > SYNC_COOLDOWN_MS) {
        console.log('[Chain] Balance mismatch detected — syncing Firestore:', { app: currentAppBalance, chain: chainBalance });
        const userId = getUserId();
        api.syncBalance(userId, chainBalance, 'on-chain balance sync')
          .then((result) => {
            console.log('[Chain] Firestore synced to chain balance:', result);
            set((state) => ({
              wallet: state.wallet
                ? { ...state.wallet, balanceJPYC: chainBalance, balanceUSD: toBalanceUSD(chainBalance) }
                : null,
            }));
            // Add adjustment transaction to local state if one was created
            if (result.transaction_id) {
              const diff = result.adjustment;
              set((state) => ({
                transactions: [
                  {
                    id: result.transaction_id,
                    type: 'adjustment' as const,
                    amount: Math.abs(diff),
                    fee: 0,
                    timestamp: new Date(),
                    status: 'completed' as const,
                  },
                  ...state.transactions,
                ],
              }));
            }
          })
          .catch((err) => console.error('[Chain] Firestore sync failed:', err));
      }
    } catch (err) {
      console.error('[Chain] Balance query failed:', err);
      set((state) => ({
        wallet: state.wallet
          ? { ...state.wallet, chainBalanceJPYC: null }
          : null,
      }));
    }
  },

  // Card operations: optimistic UI update + background DB sync
  addCard: (card: Omit<CreditCard, 'id'>) => {
    const tempId = 'card_' + Math.random().toString(36).substring(2, 8);
    set((state) => {
      const isFirst = state.cards.length === 0;
      return {
        cards: [...state.cards, { ...card, id: tempId, isDefault: isFirst || card.isDefault }],
      };
    });
    // Sync to DB in background
    api.addCard(getUserId(), {
      brand: card.brand,
      last4: card.last4,
      expiry: card.expiry,
      name: card.name,
      is_default: card.isDefault,
    }).then((res) => {
      // Replace temp ID with DB ID
      set((state) => ({
        cards: state.cards.map((c) => c.id === tempId ? { ...c, id: res.card_id } : c),
      }));
    }).catch((err) => console.error('[DB] Add card failed:', err));
  },

  removeCard: (id: string) => {
    set((state) => {
      const remaining = state.cards.filter((c) => c.id !== id);
      if (remaining.length > 0 && !remaining.some((c) => c.isDefault)) {
        remaining[0].isDefault = true;
      }
      return { cards: remaining };
    });
    api.removeCard(getUserId(), id)
      .catch((err) => console.error('[DB] Remove card failed:', err));
  },

  setDefaultCard: (id: string) => {
    set((state) => ({
      cards: state.cards.map((c) => ({ ...c, isDefault: c.id === id })),
    }));
    api.setDefaultCard(getUserId(), id)
      .catch((err) => console.error('[DB] Set default card failed:', err));
  },

  // Favorites (#1078): 店単位のお気に入り。login / restoreSession で hydrate。
  loadFavorites: async () => {
    const userId = getUserId();
    try {
      const res = await api.getFavorites(userId);
      set({ favoriteStores: (res?.favorites || []).map(mapFavorite) });
    } catch (err) {
      console.error('[Favorites] load failed:', err);
    }
  },

  // 楽観更新（即トグル）→ 背景で DB 同期。doc id = storeId なので冪等。
  toggleFavorite: (store: Store) => {
    const exists = get().favoriteStores.some((f) => f.storeId === store.id);
    if (exists) {
      set((state) => ({
        favoriteStores: state.favoriteStores.filter((f) => f.storeId !== store.id),
      }));
      api.removeFavorite(getUserId(), store.id)
        .catch((err) => console.error('[DB] Remove favorite failed:', err));
    } else {
      const fav: FavoriteStore = {
        storeId: store.id,
        name: store.name,
        nameEn: store.nameEn,
        imageUrl: store.imageUrl,
        category: store.category,
        genre: genreOf(store),
      };
      set((state) => ({ favoriteStores: [...state.favoriteStores, fav] }));
      api.addFavorite(getUserId(), store.id, {
        name: store.name,
        name_en: store.nameEn,
        image_url: store.imageUrl,
        category: store.category,
        genre: genreOf(store),
      }).catch((err) => console.error('[DB] Add favorite failed:', err));
    }
  },

  // Update expense tag on a payment transaction (in-memory; Firestore sync in Phase 2)
  setExpenseTag: (txId, tag, category, memo) => {
    set((state) => ({
      transactions: state.transactions.map((tx) =>
        tx.id === txId
          ? {
              ...tx,
              expenseTag: tag ?? undefined,
              expenseCategory: tag === 'business' ? (category || undefined) : undefined,
              expenseMemo: tag === 'business' ? (memo || undefined) : undefined,
            }
          : tx
      ),
    }));
  },

  setBudget: (enabled: boolean, amount: number) => {
    const wasEnabled = get().budgetEnabled;
    const now = Date.now();
    const existingStartedAt = get().budgetStartedAt;
    const startedAt = (!wasEnabled && enabled && !existingStartedAt) ? now : existingStartedAt || now;
    const notified80 = (!wasEnabled && enabled) ? false : get().budgetNotified80;
    const notified100 = (!wasEnabled && enabled) ? false : get().budgetNotified100;
    set({ budgetEnabled: enabled, budgetAmount: amount, budgetStartedAt: startedAt, budgetNotified80: notified80, budgetNotified100: notified100 });
    localStorage.setItem('budgetEnabled', String(enabled));
    localStorage.setItem('budgetAmount', String(amount));
    localStorage.setItem('budgetStartedAt', String(startedAt));
    localStorage.setItem('budgetNotified80', String(notified80));
    localStorage.setItem('budgetNotified100', String(notified100));
  },

  resetBudget: () => {
    const now = Date.now();
    set({ budgetStartedAt: now, budgetNotified80: false, budgetNotified100: false });
    localStorage.setItem('budgetStartedAt', String(now));
    localStorage.setItem('budgetNotified80', 'false');
    localStorage.setItem('budgetNotified100', 'false');
  },

  getBudgetSpending: () => {
    const { transactions, budgetStartedAt } = get();
    return transactions
      .filter((tx) => tx.type === 'payment' && tx.status === 'completed' && new Date(tx.timestamp).getTime() >= budgetStartedAt)
      .reduce((sum, tx) => sum + tx.amount, 0);
  },

  markBudgetNotified: (level: 80 | 100) => {
    if (level === 80) {
      set({ budgetNotified80: true });
      localStorage.setItem('budgetNotified80', 'true');
    } else {
      set({ budgetNotified100: true });
      localStorage.setItem('budgetNotified100', 'true');
    }
  },

  setCurrentStore: (store: Store | null) => set({ currentStore: store }),
  // #968 現金会計の承認検知時に呼ぶ。カード成功時 (paySelfOrderCheckout) と同じ
  // 支払いコンテキストを張り、/payment/complete を現金でも成立させる。取引レコード
  // 自体は店舗承認で backend が作成済み（次の fetch で transactions に載る）。
  // #1397 その取引を後から特定できるよう、締められた注文 ID を控える
  // (client は現金取引の ID を知らないため、tx.orderIds と突き合わせる)。
  markCashPaymentComplete: (store, amount, orderIds = []) => set({
    currentStore: store,
    lastPaymentAmount: amount,
    lastPaymentFee: 0,
    lastPaymentMethod: 'cash',
    lastPaymentTxHash: null,
    lastPaymentExplorerLink: null,
    lastPaymentTxId: null,
    lastPaymentOrderIds: orderIds,
    lastTxTimestamp: Date.now(),
  }),

  setDietaryPreferences: (allergens: string[], styles: string[]) => {
    const state = get();
    // #2115 **ゲストでも保存できるようにする。** 卓の QR から入る訪日のお客様は
    // ログインしないまま見るので、以前はここで弾かれ、アレルギーの絞り込みを
    // 一番使ってほしい人が使えなかった (ログインしていないと何も起きない)。
    // 端末に残し、ログインしていればプロフィールにも反映する。
    set({
      guestDietaryAllergens: allergens,
      guestDietaryStyles: styles,
      ...(state.user
        ? { user: { ...state.user, dietaryAllergens: allergens, dietaryStyles: styles } }
        : {}),
    });
    try {
      localStorage.setItem('dietaryAllergens', JSON.stringify(allergens));
      localStorage.setItem('dietaryStyles', JSON.stringify(styles));
    } catch {
      // ignore storage errors
    }
  },

  loadStores: async () => {
    try {
      const data = await api.getStores();
      if (data.stores) set({ stores: data.stores });
    } catch (e) {
      console.error('[Store] loadStores failed:', e);
    }
  },

  applyMenuItemSoldOut: (storeId, menuItemId, soldOut) => {
    set((state) => ({
      stores: state.stores.map((s) =>
        s.id === storeId
          ? { ...s, menu: (s.menu || []).map((m) => (m.id === menuItemId ? { ...m, soldOut } : m)) }
          : s),
    }));
  },

  // Store management actions (API mode: persist to Firestore, demo mode: local only)
  // #1373 以前は失敗を握り潰して void を返していたため、**保存できていないのに
  // 呼び出し側が成功トーストを出していた**（403 で弾かれた設定が「保存しました」と
  // 表示され、リロードで元に戻る）。結果を返し、失敗時は楽観更新も巻き戻す。
  hydrateStore: (storeId: string, data: Partial<Store>) => {
    set((state) => ({
      stores: state.stores.map((s) => (s.id === storeId ? { ...s, ...data } : s)),
    }));
  },

  updateStore: async (storeId: string, updates: Partial<Store>) => {
    const before = get().stores.find((s) => s.id === storeId);
    set((state) => ({
      stores: state.stores.map((s) => (s.id === storeId ? { ...s, ...updates } : s)),
    }));
    if (import.meta.env.VITE_API_URL) {
      try {
        await api.updateStore(storeId, updates);
      } catch (e) {
        console.error('[Store] updateStore API failed:', e);
        if (before) {
          set((state) => ({
            stores: state.stores.map((s) => (s.id === storeId ? before : s)),
          }));
        }
        return false;
      }
    }
    return true;
  },

  // #1439 スタッフに見せる範囲。**オーナー専用の別エンドポイント**を叩く
  // （updateStore = owner|manager に相乗りさせない）。店長が開いた場合は 403 に
  // なるので、updateStore と同じく楽観更新を巻き戻して false を返す。
  updateStoreRolePolicy: async (storeId: string, rolePolicy: RolePolicy) => {
    const before = get().stores.find((s) => s.id === storeId);
    set((state) => ({
      stores: state.stores.map((s) => (s.id === storeId ? { ...s, rolePolicy } : s)),
    }));
    if (import.meta.env.VITE_API_URL) {
      try {
        await api.updateStoreRolePolicy(storeId, rolePolicy as Record<string, Record<string, boolean>>);
      } catch (e) {
        console.error('[Store] updateStoreRolePolicy API failed:', e);
        if (before) {
          set((state) => ({
            stores: state.stores.map((s) => (s.id === storeId ? before : s)),
          }));
        }
        return false;
      }
    }
    return true;
  },

  addMenuItem: async (storeId: string, item: MenuItem) => {
    set((state) => ({
      stores: state.stores.map((s) =>
        s.id === storeId ? { ...s, menu: [...s.menu, item] } : s
      ),
    }));
    if (import.meta.env.VITE_API_URL) {
      try {
        const result = await api.addMenuItem(storeId, item);
        // Update local ID with server-generated ID
        set((state) => ({
          stores: state.stores.map((s) =>
            s.id === storeId
              ? { ...s, menu: s.menu.map((m) => (m.id === item.id ? { ...m, id: result.id } : m)) }
              : s
          ),
        }));
      } catch (e) { console.error('[Store] addMenuItem API failed:', e); }
    }
  },

  updateMenuItem: async (storeId: string, itemId: string, updates: Partial<MenuItem>) => {
    const before = get().stores.find((s) => s.id === storeId)?.menu.find((m) => m.id === itemId);
    set((state) => ({
      stores: state.stores.map((s) =>
        s.id === storeId
          ? { ...s, menu: s.menu.map((m) => (m.id === itemId ? { ...m, ...updates } : m)) }
          : s
      ),
    }));
    if (import.meta.env.VITE_API_URL) {
      try {
        await api.updateMenuItem(storeId, itemId, updates);
      } catch (e) {
        // #1910 以前は console に出すだけで呼び出し側に伝わらず、保存に失敗しても画面は
        // 「更新しました」と出て、楽観更新した値も残っていた (リロードで元に戻る)。
        console.error('[Store] updateMenuItem API failed:', e);
        if (before) {
          set((state) => ({
            stores: state.stores.map((s) =>
              s.id === storeId ? { ...s, menu: s.menu.map((m) => (m.id === itemId ? before : m)) } : s
            ),
          }));
        }
        return false;
      }
    }
    return true;
  },

  deleteMenuItem: async (storeId: string, itemId: string) => {
    set((state) => ({
      stores: state.stores.map((s) =>
        s.id === storeId ? { ...s, menu: s.menu.filter((m) => m.id !== itemId) } : s
      ),
    }));
    if (import.meta.env.VITE_API_URL) {
      try { await api.deleteMenuItem(storeId, itemId); } catch (e) { console.error('[Store] deleteMenuItem API failed:', e); }
    }
  },

  resetState: () => {
    clearLocalStoragePreservingDeviceId();
    set({
      isLoggedIn: false,
      userId: null,
      user: null,
      wallet: null,
      pendingWalletAddress: null,
      cards: [],
      favoriteStores: [],
      transactions: [],
      currentStore: null,
      lastPaymentAmount: 0,
      lastPaymentFee: 0,
      lastPaymentMethod: null,
      lastPaymentTxId: null,
      lastPaymentOrderIds: [],
      lastChargeAmount: 0,
      storeStats: {
        visitors: 0,
        revenue: 0,
        countries: [],
      },
      cart: null,
    });
  },

  // Self-order cart (#289)
  cart: null,
  addToCart: (store, item, selection) => {
    const current = get().cart;
    const next: Cart = current && current.storeId === store.id
      ? { ...current }
      : { storeId: store.id, storeName: store.name, items: [] };
    // #2031 同じ商品でも選んだ内容が違えば別の行 (もも・塩 / もも・たれ)
    const lineId = cartLineId(item.id, selection);
    const existing = next.items.find((i) => cartLineOf(i) === lineId);
    if (existing) {
      // #1583 放題プランは「数量 = 人数」なので上限が店舗設定で変わる
      existing.quantity = Math.min(existing.quantity + 1, maxQuantityFor(item.id, store));
      next.items = next.items.map((i) => cartLineOf(i) === lineId ? existing : i);
    } else {
      // **行の鍵を持たせるかは「選択があるか」で決める** (表示文字列の有無ではない)。
      // 文字列が空のときに鍵を落とすと、探す鍵 (商品 ID#…) と持っている鍵 (商品 ID) が
      // 食い違い、押すたびに同じ行が増え、選択もサーバへ送られなくなる。
      const hasSelection = lineId !== item.id;
      const groups = groupsForItem(store, item);
      // #2156 カートの表示は客の言語で (注文後はサーバが焼いた訳に切り替わる)
      const variationText = selectionText(groups, selection, normalizeLocale(i18n.language));
      const optionsPrice = selectionPrice(groups, selection);
      next.items = [
        ...next.items,
        // nameEn はサーバの CartItem で**必須**。undefined のまま送ると注文が
        // 丸ごと 422 で落ちる (#1353 で放題プランの注文が失敗したのがこれ)。
        // 英名を持たない item は正当に存在するので、境界で '' に倒す。
        // #1857 カートに入れた瞬間に日本語へ戻らないよう、訳文もここで解決して持つ
        // (注文後はサーバが menu から引き直して焼き込む)。
        {
          menuItemId: item.id, name: item.name, nameEn: item.nameEn ?? '',
          nameLocalized: localizedNameFor(item), price: item.price + optionsPrice,
          imageUrl: item.imageUrl, quantity: 1,
          ...(hasSelection ? { lineId, selectedVariations: selection, variationText } : {}),
          ...(optionsPrice ? { optionsPrice } : {}),
        },
      ];
    }
    set({ cart: next });
  },
  reorderToCart: (store, items, tableNumber) => {
    const current = get().cart;
    // 同一店舗の dine-in カートがあれば数量をマージ。違う店舗/delivery なら作り直す。
    const base: Cart = current && current.storeId === store.id && current.orderType !== 'delivery'
      ? { ...current, items: current.items.map((i) => ({ ...i })) }
      : { storeId: store.id, storeName: store.name, items: [] };
    for (const it of items) {
      // #2031 前回の選択を組み直す。店が定義を変えていたらサーバが弾く (invalid_variation)
      // ので、ここでは黙って落とさない — 「塩かたれか分からない焼き鳥」を厨房へ流さない。
      const selection = it.selectedVariations ?? selectionFromSnapshot(it.variations);
      const lineId = cartLineId(it.menuItemId, selection);
      const existing = base.items.find((i) => cartLineOf(i) === lineId);
      if (existing) {
        existing.quantity = Math.min(
          existing.quantity + it.quantity, maxQuantityFor(it.menuItemId, store));
      } else {
        const menuItem = store.menu?.find((m) => m.id === it.menuItemId);
        // 店の定義がまだ手元に無い (注文履歴から開いた直後) ときは、注文に焼き込まれた名前で出す。
        // 何も出さないと「もも」が 2 行並ぶだけで、どちらが塩か客に分からない。
        const groups = groupsForItem(store, menuItem);
        const variationText = selectionText(groups, selection, normalizeLocale(i18n.language))
          || snapshotText(it.variations, true) || it.variationText || '';
        // 前回の明細の price は 本体 + 追加。店の定義が手元にあれば**今の追加料金**で見積もり直し、
        // 無ければ前回のまま持つ (どちらでも請求額はサーバが今の定義で決める)。
        // **放題で 0 円になった行の price を本体にしない。** 前回が飲み放題中 (本体 0 円) でも、
        // 次は放題の無い注文かもしれない。今のメニューの値段 → 前回の通常価格 → 前回の本体、の順。
        const basePrice = menuItem?.price ?? it.listPrice ?? (it.price - (it.optionsPrice ?? 0));
        const optionsPrice = groups.length ? selectionPrice(groups, selection) : (it.optionsPrice ?? 0);
        base.items.push({
          menuItemId: it.menuItemId, name: it.name, nameEn: it.nameEn,
          price: basePrice + optionsPrice, imageUrl: it.imageUrl,
          quantity: Math.min(it.quantity, maxQuantityFor(it.menuItemId, store)),
          ...(selection ? { lineId, selectedVariations: selection, variationText } : {}),
          ...(optionsPrice ? { optionsPrice } : {}),
        });
      }
    }
    base.orderType = 'dine-in';
    if (tableNumber) base.tableNumber = tableNumber;
    set({ cart: base });
  },
  updateCartQuantity: (lineId, quantity, maxQuantity) => {
    const current = get().cart;
    if (!current) return;
    if (quantity <= 0) {
      const items = current.items.filter((i) => cartLineOf(i) !== lineId);
      set({ cart: items.length ? { ...current, items } : null });
      return;
    }
    // 上限は**商品**で決まる (行ではない)。行の鍵から商品 ID を引く。
    const menuItemId = current.items.find((i) => cartLineOf(i) === lineId)?.menuItemId ?? lineId;
    // #1583 放題プランは「数量 = 人数」。99 固定だと、店舗が 1 組の最大人数を
    // 130 にしても飲み放題を 130 名でカートに入れられなかった。
    // 呼び出し側が上限を知っていれば渡してもらい、無ければ手元の店舗情報で解決する
    // (店舗が読めない時は既定 50 に倒れるので、確実な経路は maxQuantity を渡す)。
    const storeForCap = get().stores.find((s) => s.id === current.storeId);
    const clamped = Math.min(quantity, maxQuantity ?? maxQuantityFor(menuItemId, storeForCap));
    set({
      cart: {
        ...current,
        items: current.items.map((i) => cartLineOf(i) === lineId ? { ...i, quantity: clamped } : i),
      },
    });
  },
  removeFromCart: (lineId) => {
    const current = get().cart;
    if (!current) return;
    const items = current.items.filter((i) => cartLineOf(i) !== lineId);
    set({ cart: items.length ? { ...current, items } : null });
  },
  applyCartPrices: (changes) => {
    const current = get().cart;
    if (!current || changes.length === 0) return;
    const byLine = new Map(changes.map((c) => [c.lineId, c.basePrice]));
    set({
      cart: {
        ...current,
        items: current.items.map((i) => {
          const base = byLine.get(cartLineOf(i));
          return base == null ? i : { ...i, price: base + (i.optionsPrice ?? 0) };
        }),
      },
    });
  },
  clearCart: () => set({ cart: null }),
  setCartTableNumber: (store, tableNumber) => {
    const current = get().cart;
    // Scanning a table QR is a declaration of a dine-in session. If the
    // existing cart is for this same store but in delivery mode, it was
    // started from the /delivery flow — don't merge those items into a
    // dine-in bill. Reset to an empty dine-in cart. Same store + already
    // dine-in: keep items, just stamp/refresh the table.
    const keep =
      current &&
      current.storeId === store.id &&
      current.orderType !== 'delivery'
    const next: Cart = keep
      ? { ...current, tableNumber, orderType: 'dine-in' }
      : { storeId: store.id, storeName: store.name, items: [], tableNumber, orderType: 'dine-in' };
    set({ cart: next });
  },
  setCartDeliveryMode: (store, coords) => {
    const current = get().cart;
    // Same-store context switch: dine-in → delivery (or vice-versa) must
    // not carry items over. Delivery is pre-paid per-order; a lingering
    // dine-in basket would be silently pre-charged. Reset to a fresh
    // delivery cart. Keeping the cart is only safe when the existing one
    // is already delivery for this same store.
    const keep =
      current &&
      current.storeId === store.id &&
      current.orderType === 'delivery'
    const base: Cart = keep
      ? { ...current, orderType: 'delivery' }
      : { storeId: store.id, storeName: store.name, items: [], orderType: 'delivery' };
    const next: Cart = coords
      ? { ...base, deliveryLat: coords.lat, deliveryLng: coords.lng }
      : base;
    set({ cart: next });
  },
  setCartDeliveryAddress: (address, notes) => {
    const current = get().cart;
    if (!current) return;
    set({
      cart: {
        ...current,
        orderType: 'delivery',
        deliveryAddress: address,
        deliveryNotes: notes,
      },
    });
  },
}));

// Debug shortcuts - attach to window
declare global {
  interface Window {
    yokosoDemo?: {
      setBalance: (amount: number) => void;
      resetState: () => void;
      resetAnnouncements: () => void;
      skipToPayment: () => void;
      triggerQRScan: () => void;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.VITE_DEMO_MODE === 'true') {
  window.yokosoDemo = {
    setBalance: (amount: number) => useStore.getState().setBalance(amount),
    resetState: () => useStore.getState().resetState(),
    // お知らせ枠 (#1384) は「閉じたら二度と出さない」設計なので、確認のたびに
    // 出し直せる入口が要る。これが無いと、一度閉じた時点で検証が続けられない。
    resetAnnouncements: () => { resetAnnouncementState(); window.location.reload(); },
    skipToPayment: () => { window.location.href = '/scan'; },
    triggerQRScan: () => { document.dispatchEvent(new CustomEvent('yokoso-qr-scan')); },
  };
}
